/**
 * Wire types mirroring the backend's Pydantic schemas.
 *
 * Kept in sync by hand with `backend/app/schemas/`. If a contract changes, both
 * sides change in one edit -- a drifted type here fails silently at runtime
 * rather than at build time, because JSON does not typecheck itself.
 */

export type Substrate =
  | 'available'
  | 'partial'
  | 'no_substrate'
  | 'insufficient_sample'
  | 'not_configured';

export interface Population {
  described: string;
  total: number;
  measured: number;
  unmeasured: number;
  coverage: number;
}

export interface AnswerOut<T> {
  substrate: Substrate;
  value: T | null;
  /** Authored English. The fallback whenever `reason_key` is absent or untranslated. */
  reason: string | null;
  /** Translation key for `reason`, rendered with `reason_params`. */
  reason_key?: string | null;
  /** The measured numbers to interpolate. Parameters, not baked into the string,
      so the languages cannot drift from each other or from the measurement. */
  reason_params?: Record<string, string | number>;
  notes: string[];
  population: Population;
}

export interface AsOf {
  agency: string;
  /**
   * Authored English. Stays the fallback, and is what an export or a client
   * without i18n gets.
   */
  corpus_scope: string;
  /**
   * Translation key for `corpus_scope`. Null when the scope is composed from
   * more than one track (`/aneel/bidding` joins two) or has no translation
   * yet — render `corpus_scope` in that case.
   */
  scope_key: string | null;
  earliest: string | null;
  latest: string | null;
  retrieved_at: string;
  /**
   * A coverage window in YEARS, with the basis it was read on. CADE only:
   * its session archive is dated by the folder each ata is filed under and
   * its published register by Qlik's own `ano_decisao` -- two concepts that
   * must never be read as one, hence a code the strip translates rather than
   * a label chosen server-side. Takes precedence over `earliest`/`latest` on
   * screen; `/cade/activity` carries both so its export keeps `corpus_span`.
   * Optional because ANATEL and ANEEL never send it.
   */
  year_window?: YearWindow | null;
}

export interface YearWindow {
  first: number;
  last: number;
  /** `session_archive_year` | `qlik_ano_decisao` */
  basis: string;
  /** Sessions whose minuted prose also carries a four-digit year. */
  checkable?: number | null;
  /** How many of `checkable` agree with the folder year. */
  agree?: number | null;
}

export interface CategoryShare {
  code: string;
  count: number;
  share: number;
}

export interface QuantileSummary {
  n: number;
  quantiles: Record<string, number>;
  minimum: number;
  maximum: number;
  spread_ratio: number | null;
}

export interface TrendPoint {
  period: number;
  /** The denominator of every share on this point. Never render without it. */
  total: number;
  counts: Record<string, number>;
  shares: Record<string, number>;
}

export interface AnatelCorpus {
  processes: number;
  decisions: number;
  final_decisions: number;
  entities: number;
  entities_with_valid_cnpj: number;
}

export interface AnatelOverview {
  as_of: AsOf;
  corpus: AnatelCorpus;
  outcome_mix: AnswerOut<CategoryShare[]>;
  fine_distribution: AnswerOut<QuantileSummary>;
  outcome_trend: AnswerOut<TrendPoint[]>;
  /** Dispositions, NOT fine reductions -- appeal linkage is very thin. */
  appeal_dispositions: AnswerOut<CategoryShare[]>;
  /**
   * Appeal decisions linked to the decision they appeal, over the same
   * population `appeal_dispositions` divides by. Measured, because the sentence
   * that used to state it read "34 of roughly 1,500 candidates" and was wrong
   * both ways: 34 counts every row in `decision_relationships` whatever it
   * links, and no population measures ~1,500.
   */
  appeals_linked_to_parent: number;
}

export interface DecisionFilters {
  query?: string | null;
  outcome_codes?: string[];
  year_from?: number | null;
  year_to?: number | null;
  min_fine?: number | null;
  max_fine?: number | null;
  only_with_reasoning?: boolean;
  /** One legal citation, exact match — the doctrine line's drill-through. */
  citation?: string | null;
  /** One deciding office (normalized, raw fallback) — the posture line's drill-through. */
  deciding_authority?: string | null;
  limit?: number;
  offset?: number;
}

export interface DecisionListItem {
  process_number: string;
  /** Digits only. The URL-safe form: the canonical number carries / and . */
  process_key: string;
  decision_number: string | null;
  reference_date: string | null;
  deciding_authority: string | null;
  outcome_codes: string[];
  fine_brl: number | null;
  entity_name: string | null;
  has_reasoning: boolean;
}

export interface AnatelCitationLine {
  citation: string;
  /** Final decisions citing this ground. */
  decisions: number;
  /** Code occurrences; multi-label, so shares can sum past 1. */
  outcomes: CategoryShare[];
  /** Citing finals with a priced FINE row; the rows. Counts, never a sum.
      Null on tracks with no sanctions. */
  fined_decisions: number | null;
  fine_rows: number | null;
  /** Citing finals carrying a disputed tax credit — a coverage count, never a
      sum, never a fine. Null on tracks with no credits. */
  credited_decisions: number | null;
}

export interface AnatelCitationLines {
  lines: AnatelCitationLine[];
}

export interface AnatelAuthorityLine {
  /** The deciding office — normalized where the ETL managed it, raw otherwise.
      An office, never a person. */
  authority: string;
  decisions: number;
  /** Code occurrences; multi-label, so shares can sum past 1. */
  outcomes: CategoryShare[];
}

export interface AnatelAuthorityPosture {
  lines: AnatelAuthorityLine[];
  /** Every distinct office in the corpus, listed or not. */
  offices_total: number;
  /** Final decisions naming no office — counted, never dropped. */
  without_authority: number;
}

export interface DecisionList {
  as_of: AsOf;
  /** Rows MATCHING the filters, not rows returned. */
  total: number;
  limit: number;
  offset: number;
  items: DecisionListItem[];
}

/** The doctrine panel, on its own endpoint: corpus-wide and filter-independent,
    so the page fetches it once rather than with every list request. */
export interface DecisionDoctrine {
  as_of: AsOf;
  citation_lines: AnswerOut<AnatelCitationLines>;
}

/** The office-posture panel, on its own endpoint for the same reason. */
export interface DecisionAuthorityPosture {
  as_of: AsOf;
  authority_posture: AnswerOut<AnatelAuthorityPosture>;
}

export interface Sanction {
  sanction_type: string;
  amount_brl: number | null;
}

export interface Proposition {
  issue: string;
  proposition_text: string;
  legal_basis: string | null;
  /** affirms | denies */
  polarity: string;
  evidence_span: string;
  /** False means the span was NOT found verbatim in the source. Do not cite it. */
  evidence_verified: boolean;
  model_name: string;
}

export interface DocumentFilters {
  /**
   * ACCENT-SENSITIVE. The index stores accented forms, so "prescricao" and
   * "prescricao" with diacritics are different searches -- 3 results against
   * 32,299 for the same word.
   */
  query?: string | null;
  document_type?: string | null;
  process_key?: string | null;
  linkable_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface DocumentHit {
  process_number: string | null;
  /** Set only when the process exists here. Null means: render no link. */
  process_key: string | null;
  document_type: string | null;
  tematica: string | null;
  reference_date_raw: string | null;
  entity_cnpj: string | null;
  entity_uf: string | null;
  source_file: string;
  /**
   * Matches wrapped in [[hl]]...[[/hl]] sentinels, NOT HTML. ts_headline does
   * not escape the source text, so this must never be injected as markup.
   */
  snippet: string;
}

export interface DocumentSearchSummary {
  /** Exact, even when the ranking is windowed. */
  total: number;
  returned: number;
  /** Ordering is relevance within a window, not across the corpus. */
  ranking_windowed: boolean;
  window: number;
  no_searchable_terms: boolean;
  /** Query carried no diacritics; the accented spelling is a different search. */
  query_unaccented: boolean;
}

export interface DocumentSearchList {
  as_of: AsOf;
  limit: number;
  offset: number;
  search: AnswerOut<DocumentSearchSummary>;
  items: DocumentHit[];
}

export interface DocumentTypeOption {
  document_type: string;
  documents: number;
}

export interface PropositionFilters {
  /** Free text. Accents are folded on both sides by the backend. */
  query?: string | null;
  /** Exact issue string, set by clicking an issue label on a result. */
  issue?: string | null;
  /** affirms | denies */
  polarity?: string | null;
  verified_only?: boolean;
  final_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface PropositionHit {
  process_number: string;
  process_key: string;
  decision_number: string | null;
  reference_date: string | null;
  is_final_observed: boolean;
  entity_name: string | null;
  outcome_codes: string[];
  issue: string;
  proposition_text: string;
  legal_basis: string | null;
  /** affirms | denies */
  polarity: string;
  evidence_span: string;
  /** False means the span was NOT found verbatim in the source. Do not cite it. */
  evidence_verified: boolean;
  model_name: string;
  prompt_version: string;
}

export interface PropositionSearchSummary {
  /** Propositions MATCHING, not propositions returned. */
  total: number;
  decisions: number;
  verified: number;
  unverified: number;
  verified_share: number;
  denies: number;
  issues: number;
  /**
   * The query held no searchable term (all stopwords or punctuation).
   * Distinct from zero matches: the search never ran, so the empty result says
   * nothing about ANATEL and must not be rendered as though it did.
   */
  no_searchable_terms: boolean;
}

export interface PropositionSearchList {
  as_of: AsOf;
  limit: number;
  offset: number;
  search: AnswerOut<PropositionSearchSummary>;
  items: PropositionHit[];
}

export interface SimilarDecision {
  process_number: string;
  process_key: string;
  decision_number: string | null;
  reference_date: string | null;
  outcome_codes: string[];
  fine_brl: number | null;
  shared_citations: string[];
  shared_count: number;
  same_outcome: boolean | null;
  days_apart: number | null;
  verdict: string | null;
  explanation_category: string | null;
}

export interface DecisionIntelligence {
  propositions: Proposition[];
  propositions_verified: number;
  similar: SimilarDecision[];
  /** Pairs that exist, not pairs returned — the list is capped. */
  similar_total: number;
}

export interface ProcessDecision {
  decision_number: string | null;
  decision_type: string | null;
  reference_date: string | null;
  deciding_authority: string | null;
  outcome_codes: string[];
  legal_citations: string[];
  is_final_observed: boolean;
  sanctions: Sanction[];
  reasoning_subject: string | null;
  reasoning_analysis: string | null;
  reasoning_conclusion: string | null;
  /** Null when this decision has neither propositions nor a comparator pair. */
  intelligence: DecisionIntelligence | null;
}

export interface EntityListItem {
  canonical_name: string;
  /** Digits only. The natural key and the URL form. */
  cnpj: string;
  cnpj_valid: boolean | null;
  processes: number;
  decisions: number;
  fined_decisions: number;
  total_fined_brl: number | null;
  latest_decision: string | null;
}

export interface EntityList {
  as_of: AsOf;
  total: number;
  limit: number;
  offset: number;
  items: EntityListItem[];
}

export interface CitationCount {
  citation: string;
  count: number;
}

export interface FinePlacement {
  summary: QuantileSummary;
  corpus_median: number;
  corpus_n: number;
  /** Percentile of this company's MEDIAN fine within all corpus fines, 0..1. */
  median_percentile: number;
  median_ratio: number;
}

export interface EntityProfile {
  as_of: AsOf;
  canonical_name: string;
  cnpj: string;
  cnpj_valid: boolean | null;
  processes: number;
  final_decisions: number;
  earliest: string | null;
  latest: string | null;
  top_citations: CitationCount[];
  outcome_mix: AnswerOut<CategoryShare[]>;
  fine_placement: AnswerOut<FinePlacement>;
}

export interface CitationOption {
  citation: string;
  decisions: number;
  fines: number;
  /** Appears in >200 final decisions: procedural, not a fact-pattern signal. */
  is_boilerplate: boolean;
}

export interface ConditionalFines {
  population: QuantileSummary;
  corpus: QuantileSummary;
  median_ratio: number;
  p90_ratio: number;
  subject_amount: number | null;
  subject_percentile: number | null;
}

export interface SanctionIntelligence {
  as_of: AsOf;
  citations: string[];
  match_all: boolean;
  matched_decisions: number;
  distribution: AnswerOut<ConditionalFines>;
}

export interface ReviewQueue {
  verdicts: CategoryShare[];
  explanations: CategoryShare[];
  adjudicated: number;
  different_outcome_pairs: number;
  contradictions: number;
  contradictions_fully_verified: number;
  adjudicated_share: number;
}

export interface QaState {
  status: string;
  pairs: number;
  /** Lowest span ratio in the bucket. Below 1.0 the span is a paraphrase. */
  worst_match_ratio: number | null;
}

export interface EvidenceQuality {
  states: QaState[];
  contradictions: number;
  reviewed: number;
  /** Spans verbatim on BOTH sides. The only bucket safe to quote. */
  quotable: number;
  both_propositions_corroborated: number;
  corroborated_share: number;
}

export interface ConsistencyOverview {
  as_of: AsOf;
  queue: AnswerOut<ReviewQueue>;
  evidence: AnswerOut<EvidenceQuality>;
}

export interface ContradictionSide {
  process_number: string | null;
  process_key: string | null;
  decision_number: string | null;
  outcome_codes: string[];
  reference_date: string | null;
  proposition: string | null;
  evidence: string | null;
  /** False means the span was NOT found verbatim in the source. Do not cite it. */
  evidence_verified: boolean | null;
}

export interface Contradiction {
  /** Natural composite: process_a|decision_a|process_b|decision_b. Never a uuid. */
  pair_key: string;
  verdict: string;
  explanation_category: string | null;
  shared_issue: string | null;
  explanation: string | null;
  confidence: number | null;
  model_name: string;
  prompt_version: string;
  shared_citations: string[];
  fully_verified: boolean;
  /** AUTO_VERIFIED | AUTO_VERIFIED_FUZZY | NEEDS_MANUAL_REVIEW | NOT_QA_REVIEWED. */
  qa_status: string;
  /** Lower of the two span ratios; null when the QA pass never saw the row. */
  qa_worst_ratio: number | null;
  /** Both propositions independently reproduced. Separate from verification. */
  qa_propositions_corroborated: boolean;
  side_a: ContradictionSide;
  side_b: ContradictionSide;
}

export interface ContradictionList {
  as_of: AsOf;
  verdict: string;
  only_fully_verified: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Contradiction[];
}

export interface PafReviewQueue {
  verdicts: CategoryShare[];
  explanations: CategoryShare[];
  adjudicated: number;
  different_outcome_pairs: number;
  contradictions: number;
  contradictions_fully_verified: number;
  adjudicated_share: number;
}

export interface PafConsistencyOverview {
  as_of: AsOf;
  queue: AnswerOut<PafReviewQueue>;
}

export interface PafContradictionSide {
  case_number: string | null;
  case_key: string | null;
  decision_number: string | null;
  outcome_codes: string[];
  /** RAW text as stored; this corpus has no parsed date column. */
  reference_date_raw: string | null;
  /** A DISPUTED TAX CREDIT, never a fine. Must not join a sanction distribution. */
  credit_amount_brl: number | null;
  proposition: string | null;
  evidence: string | null;
  /** False means the span was NOT found verbatim in its source. Do not cite it. */
  evidence_verified: boolean | null;
}

export interface PafContradiction {
  /** Natural composite: case_a|decision_a|case_b|decision_b. Never a uuid. */
  pair_key: string;
  verdict: string;
  explanation_category: string | null;
  shared_issue: string | null;
  explanation: string | null;
  confidence: number | null;
  model_name: string;
  prompt_version: string;
  shared_citations: string[];
  fully_verified: boolean;
  side_a: PafContradictionSide;
  side_b: PafContradictionSide;
}

export interface PafContradictionList {
  as_of: AsOf;
  verdict: string;
  only_fully_verified: boolean;
  total: number;
  limit: number;
  offset: number;
  items: PafContradiction[];
}

export interface Track {
  key: string;
  processes: number;
  decisions: number;
}

export interface CorpusCoverage {
  tracks: Track[];
  registry_processes: number;
  registry_documents: number;
  registry_adjudicated: number;
  documents_by_tier: Record<string, number>;
  analysed_processes: number;
  adjudicative_share: number;
  routine_share: number;
  /** Held processes over ADJUDICATIVE processes — the ratio that matters. */
  adjudicative_coverage: number;
}

export interface Coverage {
  as_of: AsOf;
  coverage: AnswerOut<CorpusCoverage>;
}

export interface PafCorpus {
  cases: number;
  decisions: number;
  final_decisions: number;
  entities: number;
  classified: number;
  with_credit: number;
}

export interface RelatorOutcomes {
  relator: string;
  /** URL key for `/anatel/r/:slug`, derived from the name, never a row uuid. */
  slug: string;
  /** Every decision carrying this relator. */
  decisions: number;
  /** Of those, the ones carrying an outcome code -- the denominator of `shares`. */
  classified: number;
  shares: CategoryShare[];
  /** False below the decision floor: the row keeps its counts but shows no rate. */
  eligible: boolean;
}

export interface RelatorDecision {
  decision_number: string | null;
  process_key: string;
  process_number: string;
  reference_date: string | null;
  outcome_codes: string[];
  credit_amount_brl: number | null;
  is_final_observed: boolean;
}

export interface RelatorDissent {
  process_number: string | null;
  reference_date: string | null;
  reasoning_excerpt: string | null;
}

/**
 * Dissents as a COUNT.
 *
 * There is no vote denominator here and there must never be one: two numbers on
 * a one-person page are a rate, and this table records only departures from
 * unanimity. See `analytics/anatel/paf.py::RelatorCollegiate`.
 */
export interface RelatorCollegiate {
  dissents: number;
  below_listing_floor: boolean;
  records: RelatorDissent[];
}

export interface PersonProfile {
  as_of: AsOf;
  name: string;
  slug: string;
  decisions_total: number;
  classified_total: number;
  earliest: string | null;
  latest: string | null;
  decisions: AnswerOut<RelatorDecision[]>;
  outcome_mix: AnswerOut<CategoryShare[]>;
  corpus_mix: AnswerOut<CategoryShare[]>;
  credit: AnswerOut<QuantileSummary>;
  collegiate: AnswerOut<RelatorCollegiate>;
  /** The Conselho Diretor track: model-derived panels read from registered runs. */
  conselho: PersonConselho;
}

export interface PafOverview {
  as_of: AsOf;
  corpus: PafCorpus;
  /** PADO classified share over ALL decisions — same population as the PAF
      stat beside it, so the comparison is like-for-like. Null if PADO is empty. */
  pado_classified_share: number | null;
  outcome_mix: AnswerOut<CategoryShare[]>;
  /** Disputed TAX CREDITS, never fines. The caveat rides on the answer's notes. */
  credit_distribution: AnswerOut<QuantileSummary>;
  relators: AnswerOut<RelatorOutcomes[]>;
}

export interface PafDecisionItem {
  process_number: string;
  process_key: string;
  decision_number: string | null;
  /** Raw ISO-ish string; PAF dates are not parsed to a date column upstream. */
  reference_date_raw: string | null;
  relator: string | null;
  forum_deliberativo: string | null;
  outcome_codes: string[];
  /** Disputed TAX CREDIT, never a fine. */
  credit_amount_brl: number | null;
  entity_name: string | null;
}

export interface PafDecisionList {
  as_of: AsOf;
  total: number;
  limit: number;
  offset: number;
  items: PafDecisionItem[];
}

/** PAF's doctrine panel, on its own endpoint like PADO's. */
export interface PafDecisionDoctrine {
  as_of: AsOf;
  citation_lines: AnswerOut<AnatelCitationLines>;
}

export interface PafDecisionFilters {
  relators: string[];
  outcomes: string[];
  years: string[];
}

export interface PafCaseDecision {
  decision_number: string | null;
  decision_type: string | null;
  reference_date_raw: string | null;
  deciding_authority: string | null;
  relator: string | null;
  forum_deliberativo: string | null;
  outcome_codes: string[];
  legal_citations: string[];
  credit_amount_brl: number | null;
  ementa: string | null;
  is_final_observed: boolean;
}

export interface PafComparable {
  process_number: string;
  process_key: string;
  decision_number: string | null;
  reference_date_raw: string | null;
  relator: string | null;
  outcome_codes: string[];
  credit_amount_brl: number | null;
  entity_name: string | null;
  shared_citations: string[];
  shared_count: number;
  /** False means the two decisions landed differently. Null means one side is unclassified. */
  same_outcome: boolean | null;
}

export interface PafCaseDetail {
  as_of: AsOf;
  process_number: string;
  process_key: string;
  entity_name: string | null;
  entity_cnpj: string | null;
  entity_cnpj_valid: boolean | null;
  decisions: PafCaseDecision[];
  /** Keyed by decision_number, matching the cards. Capped per decision. */
  comparables: Record<string, PafComparable[]>;
  comparable_totals: Record<string, number>;
  /** Numbers, not prose: the sentence is built in the locale files. */
  comparator_reach_measured: number;
  comparator_reach_total: number;
}

export interface PairingReach {
  /** Final decisions appearing in ANY comparator pair — corpus-level reach. */
  paired_finals: number;
  final_decisions: number;
}

export interface ProcessDetail {
  as_of: AsOf;
  process_number: string;
  process_key: string;
  entity_name: string | null;
  entity_cnpj: string | null;
  entity_cnpj_valid: boolean | null;
  pairing: PairingReach;
  decisions: ProcessDecision[];
}

export interface AneelCorpus {
  cases: number;
  entities: number;
  /** Cases where a decision has actually been taken. */
  resolved: number;
  /** No decision yet. Around four fifths of the corpus. */
  pending: number;
  unclassified: number;
  with_original_amount: number;
}

export interface TrajectoryMix {
  trajectories: CategoryShare[];
  total_cases: number;
  resolved: number;
  pending: number;
  unclassified: number;
  resolved_share: number;
}

export interface ReductionWaterfall {
  resolved: number;
  reduced: number;
  maintained: number;
  eliminated: number;
  increased: number;
  original_total: number;
  final_total: number;
  total_relief: number;
  /** Median share of the original penalty surviving, among REDUCED cases. */
  median_retained: number | null;
  reduced_share_of_resolved: number;
}

export interface EnforcementNature {
  nature: string;
  cases: number;
  /** Decided cases of this nature. A nature with few supports no claim. */
  resolved: number;
  /** Cases where either instance gave the appellant something; from the appeal
      record, so it can exceed `resolved`. */
  granted_any: number;
}

export interface AneelOverviewBodyRow {
  body: string;
  cases: number;
  resolved: number;
  resolved_share: number;
}

/** Who lavrou the auto; descriptive, alphabetical, never a comparison. */
export interface AneelBodySplit {
  rows: AneelOverviewBodyRow[];
  unattributed: number;
  total_cases: number;
}

/** The appeal record's headline, from the same predicate `/aneel/appeals` reads. */
export interface AneelAppealTile {
  first_minuted: number;
  board_minuted: number;
  first_granted: number;
  board_granted: number;
}

export interface AneelOverview {
  as_of: AsOf;
  corpus: AneelCorpus;
  trajectory_mix: AnswerOut<TrajectoryMix>;
  /** The screen no other agency in this product can support. */
  reduction_waterfall: AnswerOut<ReductionWaterfall>;
  penalty_distribution: AnswerOut<QuantileSummary>;
  natures: EnforcementNature[];
  body_split: AnswerOut<AneelBodySplit>;
  appeals: AneelAppealTile;
  /**
   * The three blueprint analyses this corpus cannot support. They travel in the
   * payload so the screen can show them refused; a missing panel is
   * indistinguishable from one nobody built.
   */
  decision_makers: AnswerOut<null>;
  reasoning_layer: AnswerOut<null>;
  precedent_graph: AnswerOut<null>;
}

/**
 * Four counts describing four processes, which deliberately do not agree.
 *
 * A case can be closed with no decision, carry a minuted decision that moved no
 * penalty, or be under collection while undecided. None of these is "the"
 * resolved count.
 */
export interface AneelStatusAxes {
  total_cases: number;
  decided: number;
  decision_recorded: number;
  closed: number;
  collection_linked: number;
  recorded_but_undecided: number;
  closed_but_undecided: number;
  collected_but_undecided: number;
}

export interface AneelConcentrationBand {
  label: string;
  entities: number;
}

export interface AneelEntityConcentration {
  entities: number;
  cases: number;
  bands: AneelConcentrationBand[];
  most_cases_for_one_entity: number;
  single_case_share: number;
}

export interface AneelCaseRow {
  notice_number: string;
  /** The notice number with `/` replaced by `~`. Never a uuid, and never the
      digit string, which collides across 545 of these cases. */
  case_key: string;
  notice_date: string | null;
  entity_name: string | null;
  entity_cnpj: string | null;
  nature: string | null;
  inspecting_agency: string | null;
  bands: string[];
  trajectory: string | null;
  original_amount: number | null;
  /** On an undecided case this is the amount CHARGED, carried forward by the
      source. It is only an outcome where `decided` is true. */
  final_amount: number | null;
  decided: boolean;
  decision_recorded: boolean;
  closed: boolean;
  collection_linked: boolean;
}

export interface AneelFilterOption {
  value: string;
  cases: number;
}

export interface AneelCaseList {
  as_of: AsOf;
  total: number;
  items: AneelCaseRow[];
  status_axes: AnswerOut<AneelStatusAxes>;
  concentration: AnswerOut<AneelEntityConcentration>;
  options: Record<string, AneelFilterOption[]>;
}

/**
 * The literals the case list's `first_outcome`/`board_outcome` filters accept
 * beside a code. Spelled once in `analytics/aneel/appeals.py`; the options the
 * API sends carry them as values, and the cross-tab uses NONE for a silent side.
 */
export const ANEEL_OUTCOME_FILTER_ANY = 'ANY';
export const ANEEL_OUTCOME_FILTER_NONE = 'NONE';

/** ONE instance's minuted rulings, as shares of the cases it ruled on. */
export interface AneelInstanceOutcomes {
  /** `first` or `board`. */
  instance: string;
  minuted: number;
  dated: number;
  with_amount: number;
  /** Multi-label: the counts can sum above `minuted`. */
  outcomes: CategoryShare[];
  unknown_codes: string[];
}

export interface AneelCrossCell {
  /** Codes joined with `+`, or `ANEEL_OUTCOME_FILTER_NONE`. */
  first: string;
  board: string;
  cases: number;
}

export interface AneelGroupOutcomes {
  label: string;
  cases: number;
  minuted_first: number;
  minuted_board: number;
  first: CategoryShare[];
  board: CategoryShare[];
  /** CASES carrying any relief code at the instance -- never a sum over codes. */
  granted_first: number;
  granted_board: number;
  granted_first_share: number;
  granted_board_share: number;
}

export interface AneelGroupTable {
  /** `nature`, `band` or `body`. Bodies are alphabetical, never ranked. */
  kind: string;
  rows: AneelGroupOutcomes[];
  suppressed_groups: number;
  suppressed_cases: number;
}

export interface AneelStageRelief {
  /** `FIRST_DECISION` or `BOARD_DECISION`, the penalty-event stage names. */
  stage: string;
  rows: number;
  from_total: number;
  to_total: number;
  relief: number;
  retained_share: number;
  reduced: number;
  same: number;
  increased: number;
}

export interface AneelPendingDisclosure {
  total_cases: number;
  pending: number;
  resolved: number;
  minuted_first: number;
  minuted_board: number;
  minuted_any: number;
  resolved_and_minuted_first: number;
  resolved_not_minuted_first: number;
  minuted_first_not_resolved: number;
  minuted_any_not_resolved: number;
  largest_pending_nature: string | null;
  largest_pending_cases: number;
}

export interface AneelAppeals {
  as_of: AsOf;
  pending: AnswerOut<AneelPendingDisclosure>;
  /** First instance, then the Diretoria. Two populations, never pooled. */
  instances: AnswerOut<AneelInstanceOutcomes>[];
  cross_tab: AnswerOut<AneelCrossCell[]>;
  by_nature: AnswerOut<AneelGroupTable>;
  by_band: AnswerOut<AneelGroupTable>;
  by_body: AnswerOut<AneelGroupTable>;
  /** The first stage, then the board stage, each over its own rows. */
  relief: AnswerOut<AneelStageRelief>[];
}

export interface AneelPenaltyStage {
  stage: string;
  amount: number | null;
  occurred_on: string | null;
  delta: number | null;
}

export interface AneelPenaltyWalk {
  stages: AneelPenaltyStage[];
  /** Stages with no recorded event. Named so the screen states their absence
      rather than drawing them as unchanged. */
  missing: string[];
  charged: number | null;
  latest: number | null;
}

export interface AneelDecisionRecord {
  first_codes: string[];
  first_on: string | null;
  board_codes: string[];
  board_on: string | null;
  unknown_codes: string[];
}

export interface AneelComparable {
  case_key: string;
  notice_number: string;
  entity_name: string | null;
  trajectory: string | null;
  original_amount: number | null;
  final_amount: number | null;
  decided_on: string | null;
  inspecting_agency: string | null;
  shared_bands: string[];
  cross_agency: boolean;
}

export interface AneelCollectionRow {
  collection_code: string | null;
  year_month: string | null;
  due_date: string | null;
  payment_date: string | null;
  days_late: number | null;
  collection_status: string | null;
  credit_status: string | null;
  amount_due: number | null;
  amount_paid: number | null;
  disambiguated_by_cnpj: boolean;
}

export interface AneelUpstreamNotification {
  notification_number: string;
  notification_date: string | null;
  reception_date: string | null;
  manifestation_date: string | null;
  decision_type: string | null;
  decision_date: string | null;
  agent_name: string | null;
}

export interface AneelBandPosition {
  band: string;
  n: number;
  /** Share of the band's charges at or below this one; null below the floor. */
  percentile: number | null;
}

/** Where a charge sits among the corpus's CHARGES -- decided or not. */
export interface AneelChargePosition {
  amount: number;
  corpus_n: number;
  corpus_percentile: number | null;
  bands: AneelBandPosition[];
  refused_bands: string[];
}

export interface AneelCaseDetail {
  as_of: AsOf;
  notice_number: string;
  case_key: string;
  notice_date: string | null;
  entity_name: string | null;
  entity_cnpj: string | null;
  nature: string | null;
  inspecting_agency: string | null;
  inspected_object: string | null;
  inspection_process_number: string | null;
  process_number: string | null;
  legal_classification: string | null;
  bands: string[];
  trajectory: string | null;
  original_amount: number | null;
  final_amount: number | null;
  closure_date: string | null;
  closure_term: string | null;
  decided: boolean;
  decision_recorded: boolean;
  closed: boolean;
  collection_linked: boolean;
  penalty_walk: AnswerOut<AneelPenaltyWalk>;
  decision_record: AnswerOut<AneelDecisionRecord>;
  charge_position: AnswerOut<AneelChargePosition>;
  comparables: AnswerOut<AneelComparable[]>;
  /** Instalment grain, never case grain. The count is rendered client-side so
      it reaches the reader in their own language. */
  collection: AneelCollectionRow[];
  notification: AneelUpstreamNotification | null;
}

export interface AneelConsistencyFunnel {
  total_pairs: number;
  both_sides_decided: number;
  divergent: number;
  cases_covered: number;
  decided_cases: number;
  cross_agency: number;
  single_band: number;
  case_coverage: number;
  cross_agency_share: number;
}

export interface AneelVerdictCoverage {
  adjudicated: number;
  divergent: number;
  share: number;
}

export interface AneelOutcomePairing {
  label: string;
  pairs: number;
  share: number;
}

export interface AneelPairSide {
  notice_number: string;
  case_key: string;
  entity_name: string | null;
  /** The full CNPJ `/aneel/e/:cnpj` accepts, or null. */
  entity_cnpj: string | null;
  trajectory: string | null;
  original_amount: number | null;
  final_amount: number | null;
  decided_on: string | null;
  inspecting_agency: string | null;
  /** Share of the charge that survived. Null where the charge was zero, which is
      a real value in this corpus rather than a missing one. */
  retained: number | null;
}

export interface AneelDivergentPair {
  /** Both notice numbers, sorted, joined by `|`. Never the pair uuid. */
  pair_key: string;
  nature: string | null;
  shared_bands: string[];
  side_a: AneelPairSide;
  side_b: AneelPairSide;
  cross_agency: boolean;
}

export interface AneelTiTrack {
  cases: number;
  with_penalty_type: number;
  with_manifestation: number;
  closed: number;
  entities: number;
  entities_with_enforcement: number;
  manifestation_share: number;
  penalty_types: CategoryShare[];
  manifestation_outcomes: CategoryShare[];
}

export interface AneelTiRow {
  intimation_number: string;
  intimation_date: string | null;
  entity_name: string | null;
  entity_cnpj: string | null;
  nature: string | null;
  penalty_types: string[];
  manifestation_outcome: string | null;
  closure_date: string | null;
}

export interface AneelTipeTrack {
  cases: number;
  detail_rows: number;
  cases_with_detail: number;
  cases_with_several_rows: number;
  entities: number;
  entities_with_enforcement: number;
  /** ROW grain: `penalty_type` describes the row, not the case. */
  penalty_types: CategoryShare[];
}

export interface AneelTipeWaterfall {
  /** `manifestation` or `board`: which two amounts are compared. */
  stage: string;
  rows: number;
  /** Rows carrying the pre-stage amount. LARGER than `rows`, deliberately. */
  rows_with_original: number;
  original_total: number;
  after_total: number;
  relief: number;
  reduced: number;
  unchanged: number;
  increased: number;
}

export interface AneelTiBoardCoverage {
  cases: number;
  with_board_outcome: number;
  share: number;
}

export interface AneelBidding {
  as_of: AsOf;
  ti: AnswerOut<AneelTiTrack>;
  /**
   * Absent from all 404 rows — outcome AND date. Not thin: absent.
   *
   * Typed rather than `null` so the day the ETL populates the column, the
   * payload already carries a shape for it.
   */
  ti_board: AnswerOut<AneelTiBoardCoverage>;
  /**
   * Structurally impossible: the table has no amount column at all. `null` here
   * is correct and means something different from `ti_board` — no future load
   * of this table answers it, so there is nothing to shape.
   */
  ti_money: AnswerOut<null>;
  /** The enforcement timeline's ladder shape, under the TI scope. */
  ti_intervals: AneelInterval[];
  ti_span: string | null;
  tipe: AnswerOut<AneelTipeTrack>;
  tipe_waterfall: AnswerOut<AneelTipeWaterfall>;
  /** The board stage: post-manifestation against post-board, over its own rows. */
  tipe_board_waterfall: AnswerOut<AneelTipeWaterfall>;
  sanction_months: AnswerOut<AneelSanctionMonths>;
  tipe_span: string | null;
}

export interface AneelTypeMonths {
  penalty_type: string;
  n: number;
  /** Null below the floor. */
  summary: QuantileSummary | null;
}

/** Months the bidding restrictions were set for, per row type, at issue. */
export interface AneelSanctionMonths {
  types: AneelTypeMonths[];
  board_rows: number;
}

/** One TIPE detail row: a STAGE of a case as the source wrote it. */
export interface AneelTipeRow {
  edital_number: string | null;
  inspection_process_number: string | null;
  lavratura_date: string | null;
  entity_name: string | null;
  entity_cnpj: string | null;
  penalty_type: string | null;
  original_fine: number | null;
  original_suspension_months: number | null;
  guarantee_type: string | null;
  manifestation_outcome: string | null;
  manifestation_decision_date: string | null;
  fine_after_manifestation: number | null;
  board_outcome: string | null;
  board_decision_date: string | null;
  fine_after_board: number | null;
}

export interface AneelTipeList {
  total: number;
  items: AneelTipeRow[];
  options: Record<string, AneelFilterOption[]>;
}

export interface AneelTiList {
  total: number;
  items: AneelTiRow[];
  options: Record<string, AneelFilterOption[]>;
}

export interface AneelInterval {
  stage: string;
  summary: AnswerOut<QuantileSummary>;
  /** Cases whose dates run backwards — a source inconsistency, carried rather
      than filtered, and counted per step. */
  impossible: number;
}

export interface AneelCohort {
  year: string;
  cases: number;
  decided: number;
  decided_share: number;
}

export interface AneelCohortMaturity {
  cohorts: AneelCohort[];
  largest_year: string | null;
  largest_cases: number;
  largest_decided: number;
}

/** One inspecting body's ladder for one step, over the cases it completed. */
export interface AneelBodyInterval {
  body: string;
  completed: number;
  impossible: number;
  summary: QuantileSummary;
}

export interface AneelBodyIntervals {
  stage: string;
  /** Alphabetical, never by duration: the body is descriptive. */
  rows: AneelBodyInterval[];
  suppressed_bodies: number;
  suppressed_cases: number;
}

export interface AneelStageByBody {
  stage: string;
  /** Refuses when no body completed the floor of cases at this step. */
  by_body: AnswerOut<AneelBodyIntervals>;
}

export interface AneelTimeline {
  as_of: AsOf;
  intervals: AneelInterval[];
  /** The same three steps split by who lavrou the auto; descriptive, alphabetical. */
  by_body: AneelStageByBody[];
  cohorts: AnswerOut<AneelCohortMaturity>;
  /** Always refuses. A rate by notice year measures case age, not behaviour. */
  outcome_trend: AnswerOut<null>;
}

export interface AneelEscalationFunnel {
  notifications: number;
  escalated: number;
  other: number;
  escalated_linked: number;
  other_linked: number;
  total_cases: number;
  cases_without_notification: number;
  escalation_share: number;
  link_share: number;
}

export interface AneelOtherTrackCoverage {
  other: number;
  other_linked: number;
  share: number;
}

export interface AneelNotificationYear {
  year: string;
  notifications: number;
  escalated: number;
  linked: number;
}

export interface AneelBodyRow {
  body: string;
  notifications: number;
  escalated: number;
  linked: number;
  /** Of this body's OWN inspections. */
  escalation_share: number;
}

export interface AneelBodyFunnel {
  /** Alphabetical, never by share. */
  rows: AneelBodyRow[];
  suppressed_bodies: number;
  suppressed_notifications: number;
  unattributed: number;
}

export interface AneelNotifications {
  as_of: AsOf;
  funnel: AnswerOut<AneelEscalationFunnel>;
  /** Notification to decision (overall and per what it became) and reception
      to manifestation, over rows carrying both dates. */
  intervals: AneelInterval[];
  /** The funnel per inspecting body; descriptive, alphabetical. */
  by_body: AnswerOut<AneelBodyFunnel>;
  /** Refuses today: no non-escalated instrument is loaded, so their zero
      linkage measures the ETL rather than the agency. */
  other_track: AnswerOut<AneelOtherTrackCoverage>;
  by_year: AnswerOut<AneelNotificationYear[]>;
  by_nature: CategoryShare[];
}

export interface AneelCollectionReach {
  linked_rows: number;
  linked_cases: number;
  total_cases: number;
  total_rows: number;
  unmatched_rows: number;
  ambiguous_rows: number;
  case_coverage: number;
  row_coverage: number;
}

export interface AneelStatusCell {
  trajectory: string;
  credit_status: string;
  /** Billing entries. A payment plan is many rows for one case. */
  rows: number;
  /** Distinct cases. These deliberately do NOT sum to the linked total. */
  cases: number;
}

export interface AneelAdjudicationVsCollection {
  cells: AneelStatusCell[];
  settled_but_undecided: number;
  linked_cases: number;
  case_cells_total: number;
  double_counted: number;
}

/** One credit state over linked rows, with the distinct cases it opens. */
export interface AneelCollectionCreditShare extends CategoryShare {
  cases: number;
}

/** How late the paid instalments were paid; PARTIAL by payment coverage. */
export interface AneelLateness {
  linked_rows: number;
  paid_rows: number;
  late_rows: number;
  on_time_rows: number;
  paid_cases: number;
  late_cases: number;
  late_share: number;
  /** Days late over the late rows only, or null below the floor. */
  late_days: QuantileSummary | null;
}

export interface AneelSuspensionRow {
  status: string;
  rows: number;
  cases: number;
}

/** Billing stayed by a guarantee, a deposit or a court; a state, not a verdict. */
export interface AneelSuspendedCollection {
  rows: AneelSuspensionRow[];
  cases: number;
  linked_cases: number;
  case_share: number;
}

export interface AneelCollection {
  as_of: AsOf;
  reach: AnswerOut<AneelCollectionReach>;
  cross_tab: AnswerOut<AneelAdjudicationVsCollection>;
  credit_mix: AneelCollectionCreditShare[];
  lateness: AnswerOut<AneelLateness>;
  suspended: AnswerOut<AneelSuspendedCollection>;
  billed_brl: number;
  paid_brl: number;
  /** Always refuses. Dividing billed by paid looks like a recovery rate and is
      not one: they are different quantities from different systems. */
  recovery: AnswerOut<null>;
}

export interface AneelFeeLedger {
  rows: number;
  entities: number;
  earliest: string | null;
  latest: string | null;
  entities_with_enforcement: number;
  enforcement_entities: number;
  fee_only_entities: number;
  entity_ratio: number;
}

export interface AneelPendingYear {
  year: string;
  rows: number;
  pending: number;
  pending_share: number;
}

export interface AneelPendingTrend {
  years: AneelPendingYear[];
  /** Its pending share is inflated by reconciliation lag, not behaviour. */
  latest_year: string | null;
}

export interface AneelFeeEntityCredit {
  status: string;
  rows: number;
}

export interface AneelFeeEntity {
  cnpj: string | null;
  name: string | null;
  rows: number;
  pending_rows: number;
  earliest: string | null;
  latest: string | null;
  has_enforcement: boolean;
  /** Rows per credit state other than settled; empty for most companies. */
  credit_statuses: AneelFeeEntityCredit[];
}

/** One credit state other than settled: its rows, and the companies behind them. */
export interface AneelFeeCreditReach {
  status: string;
  rows: number;
  entities: number;
  entity_share: number;
}

export interface AneelFeeExposure {
  entities_with_exposure: number;
  entities: number;
  exposure_share: number;
  statuses: AneelFeeCreditReach[];
}

export interface AneelFees {
  as_of: AsOf;
  ledger: AnswerOut<AneelFeeLedger>;
  collection_mix: CategoryShare[];
  credit_mix: CategoryShare[];
  /** Companies (not rows) per credit state other than settled. */
  exposure: AnswerOut<AneelFeeExposure>;
  /** The credit states a company can be filtered by, from the ledger itself. */
  credit_status_options: string[];
  pending: AnswerOut<AneelPendingTrend>;
  total: number;
  items: AneelFeeEntity[];
}

export interface AneelBandLadder {
  band: string;
  cases: number;
  decided: number;
  /** True where the band names REN 846/2019; false is the resolution it
      replaced. The same group number appears under both, and they are two
      regulatory regimes rather than two spellings. */
  new_regime: boolean;
  decided_share: number;
  /** The two ladders refuse independently: a band can hold 923 charges and 53
      outcomes, so the charge ladder answers while the final one cannot. */
  charged: AnswerOut<QuantileSummary>;
  final: AnswerOut<QuantileSummary>;
}

export interface AneelRegimeSplit {
  new_regime_bands: number;
  old_regime_bands: number;
  new_regime_cases: number;
  old_regime_cases: number;
}

/** One infraction nature: the conduct dimension beside the bracket dimension. */
export interface AneelNatureLadder {
  nature: string;
  cases: number;
  decided: number;
  decided_share: number;
  charged: AnswerOut<QuantileSummary>;
  final: AnswerOut<QuantileSummary>;
}

/** Where an amount sits among a population's CHARGES. */
export interface AneelPosition {
  amount: number;
  n: number;
  /** Share of the population's charges at or below `amount`. */
  percentile: number;
}

export interface AneelPositionResult {
  amount: number;
  corpus: AnswerOut<AneelPosition>;
  band: string | null;
  in_band: AnswerOut<AneelPosition> | null;
  nature: string | null;
  in_nature: AnswerOut<AneelPosition> | null;
}

export interface AneelPenalties {
  as_of: AsOf;
  bands: AneelBandLadder[];
  /** Natures carrying at least the floor of charges; `natures_omitted` counts the rest. */
  natures: AneelNatureLadder[];
  natures_omitted: number;
  /** The reference line every band is read against. */
  corpus: AnswerOut<QuantileSummary>;
  regimes: AneelRegimeSplit;
}

/** Each vocabulary over the divergent population; `cases` counts PAIRS here. */
export interface AneelPairFilterOptions {
  natures: AneelFilterOption[];
  bands: AneelFilterOption[];
  agencies: AneelFilterOption[];
  combinations: AneelFilterOption[];
}

export interface AneelConsistency {
  as_of: AsOf;
  /** Pairs matching the filters -- the same predicate the page lists. */
  total: number;
  items: AneelDivergentPair[];
  options: AneelPairFilterOptions;
  reach: AnswerOut<AneelConsistencyFunnel>;
  /** Refuses today: ANEEL publishes no reasoning text, so nothing has been
      examined for conflicting reasoning. Recomputed per request, so it answers
      instead of refusing the moment any verdict is loaded. */
  verdict_layer: AnswerOut<AneelVerdictCoverage>;
  pairings: AneelOutcomePairing[];
}

export interface CadeCorpus {
  /**
   * Every row in `cases`: the judged archive AND the Qlik-registry stubs that
   * carry no decision -- two populations. Never a headline on the tribunal
   * screen; it appears only as the secondary "in the published register" line.
   */
  cases: number;
  /** Cases carrying at least one decision: the archive's own population. */
  adjudicated_cases: number;
  decisions: number;
  final_decisions: number;
  votes: number;
  votes_canonical: number;
  commissioners: number;
  /**
   * Both pairing bases together: most key on shared outcome codes and cannot
   * diverge, a smaller citation-overlap set can. See the consistency refusal.
   */
  comparator_pairs: number;
  sessions: number;
  sessions_with_raw_date: number;
}

export interface CadeArchiveYear {
  year: number;
  sessions: number;
  final_decisions: number;
  /** Final decisions carrying at least one outcome code. */
  classified: number;
  tcc: number;
  condemned_fined: number;
  archived: number;
  /** Conditional and unconditional approvals together. */
  merger: number;
  /** Final decisions minuted "por maioria". */
  by_majority: number;
  /** BRL fine rows on FINAL decisions only -- a narrower population than the distribution's. */
  fine_rows: number;
  fines_brl: number;
}

export interface DecisionMakerSample {
  rows: number;
  people: number;
  median_observations: number;
  max_observations: number;
  rows_above_threshold: number;
  threshold: number;
}

export interface CadeCaseType {
  case_type: string;
  cases: number;
  /**
   * How many of them carry a decision.
   *
   * `cases` spans two populations that name the same concepts differently --
   * the registry writes `Finalístico: Ato de Concentração Sumário` where the
   * session archive writes `Ato de Concentração` -- so a bare total ranks 5,652
   * registry rows above 78 judged ones as though they were comparable.
   */
  adjudicated: number;
}

export interface CadeOverview {
  as_of: AsOf;
  /** The archive per source of its atas: the portal's, or rebuilt from SEI. */
  archive_sources?: CadeSourceSessions[];
  corpus: CadeCorpus;
  /**
   * The archive per filing year, counted over final decisions. The tribunal
   * archive's first time dimension; `/cade/activity` is the Qlik register, a
   * different population, and the two are never pooled.
   */
  archive_by_year: AnswerOut<CadeArchiveYear[]>;
  /** Shares of the three tracked outcomes per archive year, each over that
      year's final decisions, with the unclassified remainder as its own series. */
  outcome_trend: AnswerOut<TrendPoint[]>;
  outcome_mix: AnswerOut<CategoryShare[]>;
  fine_distribution: AnswerOut<QuantileSummary>;
  vote_positions: AnswerOut<CategoryShare[]>;
  /** Refused while the samples are thin; the reason carries the denominators. */
  agreement_matrix: AnswerOut<DecisionMakerSample>;
  rapporteur_follow_rate: AnswerOut<DecisionMakerSample>;
  /**
   * Refused as a corpus-level FIGURE, not as an impossibility.
   *
   * This read "NO_SUBSTRATE permanently: the comparator cannot express
   * disagreement", which was true of `decision_pairs` as it stood and stopped
   * being true on 2026-08-10 when a `citation_overlap` basis landed. 143 pairs
   * now survive the funnel, over 52 of 464 final decisions, and are reviewable
   * at `/cade/consistency`. A refusal is a measurement and expires like one.
   */
  consistency_review: AnswerOut<null>;
  case_types: CadeCaseType[];
  /** Distinct types that exist, so the truncated list above can say so. */
  case_types_distinct: number;
}

export interface AcordaoCorpus {
  acordaos: number;
  with_relator: number;
  votes: number;
  votes_resolved: number;
  named_people: number;
  raw_voter_strings: number;
  /** PRESENT means the member sat, NOT how they voted. */
  present_rows: number;
  dissent_rows: number;
  /**
   * Acordaos whose date the coverage window could not read. Zero today; carried
   * so the strip's window can be disclosed as partial the moment it stops being
   * complete, rather than silently narrowing.
   */
  dates_unparsed: number;
}

export interface Dissenter {
  name: string;
  /**
   * URL key for this person's page, folded server-side. Never rebuilt here: a
   * second implementation of the fold drifts on the first accented name nobody
   * tested, and on this bench that is most of them.
   */
  slug: string;
  recorded_votes: number;
  dissents: number;
}

export interface DissentRecord {
  people: Dissenter[];
  total_votes: number;
  total_dissents: number;
  named_people: number;
  votes_resolved: number;
  below_floor: number;
}

/** One or more co-dissenters who share the identical excerpt the minutes
    recorded -- grouped server-side so a joint dissent renders once, not once
    per name. */
export interface DissentDetail {
  voter: string;
  reasoning_excerpt: string;
}

export interface AcordaoItem {
  process_number: string | null;
  process_key: string | null;
  reference_date_raw: string | null;
  relator: string | null;
  forum: string | null;
  dissents: number;
  /** Present only where the minutes recorded a reasoning excerpt; measured
      2026-08-19 to be every DISSENTS row today, but never assumed complete. */
  dissent_details: DissentDetail[];
}

export interface AcordaoOverview {
  as_of: AsOf;
  corpus: AcordaoCorpus;
  dissents: AnswerOut<DissentRecord>;
  /** Refused, with the measured correlation behind the refusal. */
  dissent_rate: AnswerOut<null>;
  /**
   * Refused on measured evidence: `PRESENT` is attendance, not a vote.
   *
   * ANATEL minutes only departures from unanimity, so a rarely-minuted member
   * appears largely BECAUSE they dissented -- corr(recorded votes, dissent
   * rate) = -0.378, median 44.2% under 100 recorded votes against 0.5% at or
   * above. Stated as what was measured rather than as permanence: this repo
   * does not own the minuting convention and cannot promise it will not change.
   */
  agreement_matrix: AnswerOut<null>;
  items: AcordaoItem[];
  total: number;
  only_with_dissent: boolean;
}

/** One attachment and how much of the CADE corpus actually carries it. */
export interface CadeCoverageRow {
  attachment: string;
  decisions: number;
  total: number;
  share: number;
}

/**
 * Whether CADE party names resolve to companies. `merged` is 0 and `resolved`
 * is false: the canonicalisation collapsed nothing, so names support search and
 * display but never a count or a ranking.
 */
export interface CadePartyIdentity {
  /** The whole table, which is TWO populations. */
  parties: number;
  /** Of those, the ones named on a decision -- what every count here divides by. */
  linked: number;
  /** Party records named on no decision: CADE's published registry. */
  registry_only: number;
  distinct_names: number;
  /** Linked records whose name the ETL linked to a CNPJ on evidence. */
  with_cnpj: number;
  /** Multi-member clusters whose members carry two different CNPJ roots. */
  cnpj_cluster_conflicts?: number;
  /** Rows collapsed by NAME canonicalisation. Zero, which is the finding. */
  merged: number;
  /** Clusters holding more than one row. Navigation, never identity. */
  clusters: number;
  clustered_rows: number;
  /** Linked records whose name is a clock time the source parse misread. */
  /** Digits and punctuation only -- a clock time or a date read as a name. */
  artefact_names: number;
  resolved: boolean;
}

export interface CadeAttributionReach {
  /**
   * Every (model, prompt) that produced an attribution, never the largest alone.
   * A regex's verified span is cut from the source and matches by construction;
   * a model's is generated and the check is real. Reporting one total presents
   * the tautology as the guarantee.
   */
  methods: CadeExtractionMethod[];
  attributions: number;
  verified: number;
  unverified: number;
  by_model: number;
  by_pattern: number;
  decisions_with_attribution: number;
  decisions_with_fines: number;
}

export interface CadeCaseRow {
  case_number: string;
  case_key: string;
  case_type: string | null;
  decisions: number;
  outcome_codes: string[];
  relators: string[];
  /** Aligned with `relators` index for index -- both ordered by name in SQL. */
  relator_slugs: string[];
  fine_total_brl: number | null;
  party_count: number;
  party_sample: string[];
  /**
   * First and last archive year among the case's sessions. Replaces a
   * `min(session_date_raw)` that was an alphabetical minimum over prose.
   */
  first_session_year: number | null;
  last_session_year: number | null;
  /** CNAE sections CADE's register lists for the case; empty when it lists none. */
  sectors: string[];
}

export interface CadeSectorOption {
  code: string;
  label: string;
  /** Listed cases carrying this section. Not additive: a case can carry several. */
  cases: number;
}

/**
 * The two populations inside CADE's `cases` table.
 *
 * A bulk registry load took it from 464 rows to 8,123 while `decisions` stayed
 * at 634, so most rows carry a process number and nothing else. The two must
 * never share a denominator.
 */
export interface CadeCaseRegistry {
  cases: number;
  adjudicated: number;
  registry_only: number;
}

export interface CadeCaseList {
  as_of: AsOf;
  items: CadeCaseRow[];
  total: number;
  limit: number;
  offset: number;
  case_types: string[];
  outcome_codes: string[];
  /** Archive years of sessions that decided at least one case: the year filter's options. */
  years: number[];
  /** CNAE sections of the listed population: the sector filter's options. */
  sectors: CadeSectorOption[];
  /** Listed cases, and how many carry any sector -- the ones a sector filter can reach. */
  sector_reach: { listed: number; with_sector: number } | null;
  /** Conduct codes and UFs among SERVED coded fields; empty when the codings are not loaded. */
  coded_conducts?: string[];
  coded_ufs?: string[];
  coded_harms?: string[];
  coded_remedies?: string[];
  coded_sg_positions?: string[];
  /** Cases with at least one served coded field: what the coded filters can reach. */
  coded_reach?: number;
  /** Whether the SEI ledger is loaded, so the court-decision filter can be offered. */
  court_in_sei_available?: boolean;
  only_adjudicated: boolean;
  registry: AnswerOut<CadeCaseRegistry>;
  coverage: AnswerOut<CadeCoverageRow[]>;
  party_identity: AnswerOut<CadePartyIdentity>;
}

export interface CadeVote {
  voter: string | null;
  voter_canonical: string | null;
  /** Key for /cade/r/:slug. Null where the vote maps to no canonical person. */
  voter_slug: string | null;
  position: string;
  target: string | null;
}

export interface CadeParty {
  name: string;
  /** Key for /cade/parties/:slug. */
  slug: string;
  role: string | null;
}

export interface CadeAttributedFine {
  party_name: string;
  /** Key for /cade/parties/:slug, or null where the attribution resolves to no
      party row -- those stay plain text, never dead links. */
  party_slug: string | null;
  amount_brl: number | null;
  /** Present only when verified; an unverified span is withheld, not shown. */
  evidence_span: string | null;
  evidence_verified: boolean;
  model_name: string | null;
  prompt_version: string | null;
  /** A regex cut the span out of the source, so it verifies by construction. */
  from_pattern: boolean;
}

export interface CadeCaseDecision {
  /** URL key of this decision (/cade/votes/d/:key), from the natural key. */
  decision_key: string;
  item_number: string | null;
  relator: string | null;
  relator_canonical: string | null;
  /** URL key for `/cade/r/:slug`, folded by the backend beside the name. */
  relator_slug: string | null;
  voto_vista: string | null;
  is_unanimous: boolean | null;
  outcome_codes: string[];
  legal_citations: string[];
  decisao_found: boolean | null;
  is_final_observed: boolean;
  session_number: string | null;
  session_type: string | null;
  session_date_raw: string | null;
  /** ISO date of the session (pauta header, else the ata's prose); absent where neither states one. */
  session_date?: string | null;
  /** Archive year of the session's ata, and the ata file itself. */
  session_year: number | null;
  source_file: string | null;
  fines_brl: number[];
  attributed_fines: CadeAttributedFine[];
  votes: CadeVote[];
  parties: CadeParty[];
  /** The "Decisão:" clause, where the item carries one. */
  decisao_text: string | null;
  /** The whole ata item as minuted -- header, parties, counsel, relator, clause. */
  segment_text: string | null;
}

/** One year of CADE's published case universe. `year` null = undated bucket. */
export interface CadeYearPoint {
  year: string | null;
  processes: number;
  with_fine: number;
  /** Also in the tribunal archive. 0 means the other CADE screens are silent on this year. */
  in_archive: number;
  fines_brl: number;
  /** A year still in progress; not comparable with a completed one. */
  partial: boolean;
}

/**
 * How far the Qlik universe and the tribunal archive overlap. This screen counts
 * 8,107 published processes; every other CADE screen counts 464 adjudicated
 * tribunal cases. The two must never share a denominator.
 */
export interface CadeUniverseSplit {
  qlik_processes: number;
  in_archive: number;
  outside_archive: number;
  tribunal_cases: number;
  years_without_archive: string[];
}

export interface CadeFineConcentration {
  processes: number;
  with_fine: number;
  share_with_fine: number;
  total_brl: number;
  busiest_year: string | null;
  busiest_year_brl: number;
  busiest_year_processes: number;
}

export interface CadeCaseTypeVolume {
  case_type: string;
  processes: number;
  /** Processes of this type carrying a fine amount: the N beside the sum. */
  with_fine: number;
  fines_brl: number;
}

/** One case type's time from filing year to decision year, in whole years. */
export interface CadeDurationLadder {
  case_type: string;
  processes: number;
  /** Processes with both years, forming a span. */
  decided: number;
  /** Of `processes`, the ones the Tribunal session archive also holds. */
  in_archive: number;
  /** Null below the floor: the type is listed with its count and no ladder. */
  p25: number | null;
  median: number | null;
  p75: number | null;
  p90: number | null;
  maximum: number | null;
  /** Share of decided processes decided in their filing year. */
  share_same_year: number | null;
  below_floor: boolean;
}

export interface CadeTimeToDecision {
  ladders: CadeDurationLadder[];
  decided: number;
  undated: number;
  without_filing: number;
  negative: number;
  first_decision_year: number | null;
  last_decision_year: number | null;
}

export interface CadeActivity {
  as_of: AsOf;
  trend: AnswerOut<CadeYearPoint[]>;
  universe: AnswerOut<CadeUniverseSplit>;
  /** Refused as a trend: 215 of 8,107 processes carry an amount. */
  fines: AnswerOut<CadeFineConcentration>;
  case_types: CadeCaseTypeVolume[];
  /** Filing-to-decision ladders per case type, in whole years. */
  time_to_decision: AnswerOut<CadeTimeToDecision>;
}

export interface CadeExtractionMethod {
  model_name: string;
  prompt_version: string | null;
  attributions: number;
  verified: number;
  /** A regex extractor: its spans are cut from the source and verify trivially. */
  is_pattern: boolean;
}

export interface CadeAttributionProvenance {
  methods: CadeExtractionMethod[];
  attributions: number;
  verified: number;
  by_model: number;
  by_pattern: number;
  parties: number;
  decisions: number;
  decisions_with_fines: number;
  /** Extra rows repeating a decision, party and amount. A source duplicate. */
  duplicated: number;
}

/**
 * One minuted amount on the fine ledger: the table AS RECORDED, one row per
 * `decision_fines` row, with the case, the session, the relator, the outcome
 * and -- where exactly one attribution on the decision carries this exact
 * amount -- the party.
 */
export interface CadeFineLedgerRow {
  decision_key: string;
  case_number: string;
  case_key: string;
  item_number: string | null;
  case_type: string | null;
  session_number: string | null;
  session_year: number | null;
  session_date_raw: string | null;
  relator: string | null;
  relator_slug: string | null;
  outcome_codes: string[];
  is_final_observed: boolean;
  /** Minuted on an item the session adjourned: the amount proposed, not imposed. */
  adjourned: boolean;
  amount_brl: number | null;
  amount_ufir: number | null;
  /** Attribution rows on this decision carrying this exact amount: 0, 1, or
      several parties minuted the same amount, which the row then counts. */
  attributed_parties: number;
  /** Only when exactly one attribution matches. */
  party_name: string | null;
  party_slug: string | null;
  from_pattern: boolean | null;
  evidence_verified: boolean | null;
}

export interface CadeLedgerReach {
  rows: number;
  priced: number;
  ufir_only: number;
  adjourned: number;
  on_final: number;
  attributed: number;
  ambiguous: number;
}

/** Vocabularies read from the ledger's own rows. */
export interface CadeLedgerOptions {
  outcome_codes: string[];
  years: number[];
  case_types: string[];
}

export interface CadeFineLedger {
  as_of: AsOf;
  reach: AnswerOut<CadeLedgerReach>;
  options: CadeLedgerOptions;
  items: CadeFineLedgerRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface CadeLedgerQuery {
  outcome?: string | null;
  sessionYear?: number | null;
  caseType?: string | null;
  minBrl?: number | null;
  onlyFinal?: boolean;
  onlyAttributed?: boolean;
  limit?: number;
  offset?: number;
}

/** One outcome cohort's fine record, over final observed decisions. */
export interface CadeFineCohort {
  decisions: number;
  decisions_with_fines: number;
  /** The row-grain denominator the distribution divides by. */
  fine_rows: number;
  /** Refuses on its own while the other cohort still shows. */
  distribution: AnswerOut<QuantileSummary>;
}

/**
 * TCC homologations against condemnations, as fine cohorts -- what a
 * negotiation posture is built from. NOT a settle-vs-litigate savings figure:
 * a TCC's amounts are negotiated contributions and a condemnation's are
 * imposed sanctions, so the two are shown, never differenced.
 */
export interface CadeCohortComparison {
  tcc: CadeFineCohort;
  condemned: CadeFineCohort;
  /** Decisions carrying both codes, counted in BOTH cohorts. */
  both_codes: number;
}

export interface CadeSanctions {
  as_of: AsOf;
  fines: number;
  /** Recorded only in UFIR, so excluded from the BRL distribution. */
  ufir_only: number;
  distribution: AnswerOut<QuantileSummary>;
  cohort_comparison: CadeCohortComparison;
  provenance: AnswerOut<CadeAttributionProvenance>;
}

/** Every stage from all comparator pairs down to a reviewable divergence. */
export interface CadeConsistencyFunnel {
  total_pairs: number;
  /** On the basis that keys on shared outcomes and cannot diverge. */
  outcome_keyed_pairs: number;
  citation_pairs: number;
  both_classified: number;
  divergent: number;
  same_case_type: number;
  substantive: number;
  decisions_covered: number;
  final_decisions: number;
  decision_coverage: number;
}

/**
 * The model verdict layer. `found_nothing` is a COVERAGE statement — the model
 * had reasoning for a twentieth of the corpus — never a statement about CADE.
 */
export interface CadeVerdictSource {
  model_name: string;
  prompt_version: string | null;
  verdicts: number;
  /** Verdicts where the model said it could not tell either way. */
  insufficient: number;
  declined_share: number;
}

export interface CadeAdjudicationReach {
  verdicts: number;
  contradictions: number;
  no_contradiction: number;
  insufficient_evidence: number;
  /**
   * Verdicts holding a proposition on BOTH sides -- the only rows where a
   * contradiction was structurally findable, and the denominator the zero has
   * to be read against.
   */
  compared: number;
  compared_share: number;
  propositions: number;
  proposition_decisions: number;
  decisions: number;
  /** Reach of the SEPARATE proposition pass. Independent of these verdicts. */
  proposition_coverage: number;
  sources: CadeVerdictSource[];
  found_nothing: boolean;
}

export interface CadeConsistencyPair {
  /** Natural composite key. Never an agency row uuid. */
  pair_key: string;
  case_a: string;
  case_key_a: string;
  item_a: string | null;
  outcomes_a: string[];
  relator_a: string | null;
  relator_slug_a: string | null;
  session_date_a: string | null;
  session_year_a: number | null;
  fine_a: number | null;
  case_b: string;
  case_key_b: string;
  item_b: string | null;
  outcomes_b: string[];
  relator_b: string | null;
  relator_slug_b: string | null;
  session_date_b: string | null;
  session_year_b: number | null;
  fine_b: number | null;
  case_type: string | null;
  shared_citations: string[];
  shared_count: number;
}

export interface CadeConsistency {
  as_of: AsOf;
  queue: AnswerOut<CadeConsistencyFunnel>;
  adjudication: AnswerOut<CadeAdjudicationReach>;
  items: CadeConsistencyPair[];
  total: number;
  limit: number;
  offset: number;
  /** Case types present in the surviving queue, as filter options. */
  case_types: string[];
  /** Shared citations in the queue, most frequent first. */
  citations: string[];
}

export interface CadeConsistencyQuery {
  caseType?: string | null;
  citation?: string | null;
  limit?: number;
  offset?: number;
}

/** How far the citation comparator reaches, and what this case got. */
export interface CadeComparatorReach {
  cases: number;
  cases_with_comparables: number;
  corpus_share: number;
  shown: number;
  found: number;
  truncated: number;
}

/**
 * A decision sharing this case's legal basis. A CANDIDATE, not a finding —
 * nothing in the CADE corpus has been adjudicated for conflicting reasoning.
 */
export interface CadeComparable {
  case_number: string;
  case_key: string;
  case_type: string | null;
  item_number: string | null;
  relator: string | null;
  /** URL key for `/cade/r/:slug`, folded by the backend beside the name. */
  relator_slug: string | null;
  outcome_codes: string[];
  fine_total_brl: number | null;
  session_date_raw: string | null;
  shared_citations: string[];
  shared_count: number;
  /** False = different outcome. Null = either side unclassified. */
  same_outcome: boolean | null;
  /** The pairing does not constrain case type; false means it differs. */
  same_case_type: boolean;
  /** Either side is merely procedural, e.g. a pedido de vista. */
  procedural: boolean;
}

export interface CadeCaseDetail {
  as_of: AsOf;
  case_number: string;
  case_key: string;
  case_type: string | null;
  /** The year in the process number -- when the case was filed. */
  filing_year: number | null;
  first_session_year: number | null;
  last_session_year: number | null;
  /** Distinct sessions the case was taken to. */
  sessions: number;
  /** last_session_year minus filing_year, at year granularity; null when either is missing. */
  years_to_last_session: number | null;
  decisions: CadeCaseDecision[];
  attribution_reach: AnswerOut<CadeAttributionReach>;
  party_identity: AnswerOut<CadePartyIdentity>;
  /** Refuses for roughly seven cases in ten — the ordinary result. */
  comparables: AnswerOut<CadeComparatorReach>;
  items: CadeComparable[];
  /** Dated milestones from the published register -- a different population from the archive. */
  register: AnswerOut<CadeCaseRegister>;
  /** Coded decision fields (ETL 67): served values with quotes, or the refusal. */
  codings: AnswerOut<CadeCaseCodings>;
}

export interface OutcomeReview {
  codes: string[];
  previous_codes: string[];
  evidence: string;
  note: string;
  reviewer: string;
  reviewed_at: string;
  /** False for a drafted proposal no person has accepted yet; it reaches the ETL only once true. */
  confirmed?: boolean;
}

export interface OutcomeReviewIn {
  codes: string[];
  evidence: string;
  note: string;
}

export interface OutcomeQueueItem {
  decision_key: string;
  /** `<process>|<ata file name>|<item>`: the key the ETL's override file uses. */
  item_key: string;
  case_number: string;
  case_key: string;
  item_number: string | null;
  session_year: number | null;
  is_final: boolean;
  codes: string[];
  clause: string;
  /** The suspect rules that fired, by key. */
  rules: string[];
  /** site | sei_reconstructed */
  source?: string;
  review: OutcomeReview | null;
  /** A review exists and the archive already carries exactly its codes. */
  applied: boolean;
}

export interface OutcomeQueue {
  total: number;
  reviewed: number;
  /** Drafted proposals waiting for a person to accept them. */
  proposed?: number;
  rules: Record<string, number>;
  vocabulary: string[];
  items: OutcomeQueueItem[];
}

export interface CadeCodingQuote {
  text: string;
  /** The decision document it was found in; null when from the minutes or across two passages. */
  sei_doc_number: string | null;
  doc_type: string | null;
  doc_date: string | null;
  url: string | null;
}

export interface CadeCodedField {
  field: string;
  /** harm | market | fine | remedy | tcc | positions -- from the backend's one list. */
  group: string | null;
  /** string | string[] | number | boolean, as the ETL coded it. */
  value: string | string[] | number | boolean | null;
  quotes: CadeCodingQuote[];
}

export interface CadeCaseCodings {
  process_number: string;
  stratum: string;
  served: CadeCodedField[];
  not_stated: string[];
  /** Coded but withheld, per review reason. Never a value. */
  under_review: Record<string, number>;
  main_model: string;
  check_model: string | null;
  prompt_version: string;
  schema_version: string;
  /** The model-coded reference set and the served fields' agreement with it (ETL 67). */
  reference_cases: number;
  reference_served: number;
  reference_agreement: number;
}

export interface CadeDayLadder {
  /** The register's own procedure label, or UNRECORDED. */
  procedure: string;
  protocols: number;
  /** Protocols with both a filing date and a decision date. */
  dated: number;
  /** Days; null when the procedure is below the floor. */
  p25: number | null;
  median: number | null;
  p75: number | null;
  p90: number | null;
  maximum: number | null;
  below_floor: boolean;
}

export interface CadeDatedDurations {
  ladders: CadeDayLadder[];
  protocols: number;
  dated: number;
}

export interface CadeFlowCell {
  sg_outcome: string;
  /** The register's words for the Tribunal outcome; null when none is shown. */
  tribunal_outcome: string | null;
  protocols: number;
}

export interface CadeDecisionFlow {
  cells: CadeFlowCell[];
  sg_decided: number;
  reached_tribunal: number;
}

export interface CadeAppealCounts {
  tribunal_decided: number;
  with_appeal: number;
  without_appeal: number;
  appeal_unknown: number;
  embargos: number;
  reconsiderations: number;
}

export interface CadeAmountLadder {
  n: number;
  p25: number;
  median: number;
  p75: number;
  p90: number;
  maximum: number;
  protocols: number;
}

export interface CadeSectorCount {
  section: string;
  protocols: number;
}

/** The published register's funnels, read at protocol grain. */
export interface CadeRegister {
  durations: AnswerOut<CadeDatedDurations>;
  flow: AnswerOut<CadeDecisionFlow>;
  appeals: AnswerOut<CadeAppealCounts>;
  tcc_contributions: AnswerOut<CadeAmountLadder>;
  sectors: AnswerOut<CadeSectorCount[]>;
}

export type CadeTimelineKind =
  | 'FILED'
  | 'NOTIFIED'
  | 'SG_DECISION'
  | 'TRIBUNAL_DECISION'
  | 'TRIBUNAL_FINAL'
  | 'TRANSITED';

export interface CadeTimelineEvent {
  kind: CadeTimelineKind;
  /** ISO date, as the register dates it. */
  when: string;
  /** The register's own words for the decision. */
  outcome: string | null;
  session_number: string | null;
}

export interface CadeCaseRegister {
  rows: number;
  events: CadeTimelineEvent[];
  procedure: string | null;
  track: string | null;
  decided_by: string | null;
  rapporteur: string | null;
  appeal: string | null;
  appeal_type: string | null;
  operation_value_brl: number | null;
  /** Days from the dated filing to the latest dated decision; null unless both exist. */
  filing_to_decision_days: number | null;
  filing_to_decision_kind: CadeTimelineKind | null;
}

export interface InfracaoFilters {
  payment_status?: string | null;
  economic_group?: string | null;
  query?: string | null;
  limit?: number;
  offset?: number;
}

export interface InfracaoCorpus {
  registry_rows: number;
  registry_processes: number;
  analysed_processes: number;
  /** Analysed processes the registry reaches, via legacy_process_number_derived. */
  bridged_processes: number;
  bridged_rows: number;
  economic_groups: number;
  normas: number;
}

export interface PaymentMix {
  statuses: CategoryShare[];
  infractions: number;
  /** Does NOT reconcile with the status counts: a process can hold several states. */
  processes_touched: number;
  unrecorded_infractions: number;
  recorded_share: number;
}

export interface MoneyTotals {
  per_infraction_total: number;
  deduplicated_process_total: number;
  /** What the obvious per-row sum returns. A warning, never a figure. */
  naive_process_total: number;
  inflation_if_naive: number;
  infractions: number;
  processes: number;
}

export interface InfracaoItem {
  process_number: string;
  process_key: string;
  norma: string | null;
  dispositivo: string | null;
  decisao: string | null;
  grupo_economico: string | null;
  situacao_pagamento: string | null;
  process_status: string | null;
  multa_por_infracao_brl: number | null;
  /** The PROCESS total, repeated on every infraction row. Never sum this. */
  multa_total_processo_brl: number | null;
}

export interface InfracaoOverview {
  as_of: AsOf;
  corpus: InfracaoCorpus;
  payment_mix: AnswerOut<PaymentMix>;
  money: AnswerOut<MoneyTotals>;
  economic_groups: AnswerOut<CategoryShare[]>;
  total: number;
  limit: number;
  offset: number;
  items: InfracaoItem[];
}

/** What a CADE decision search ran over, and what it returned. */
/** A thesaurus group a query triggered: what was typed and what was searched besides it. */
export interface CadeExpansion {
  key: string;
  matched: string;
  also: string[];
}

export interface CadeSearchReach {
  matches: number;
  decisions: number;
  /** Decisions carrying the "Decisão:" clause. */
  with_text: number;
  /** Decisions carrying the whole ata item -- header, parties, counsel, clause. */
  with_item_text: number;
  /** Which text the search ran over. */
  scope: 'decision' | 'item';
  /** Reach of the scope searched, over all decisions. */
  text_coverage: number;
  /** The query held no searchable term. NOT the same as zero matches. */
  no_searchable_terms: boolean;
  text_search: boolean;
  /** Thesaurus groups the query triggered; empty or absent when searched as typed. */
  expanded?: CadeExpansion[];
}

export interface CadeDecisionHit {
  case_number: string;
  case_key: string;
  item_number: string | null;
  relator: string | null;
  /** URL key for `/cade/r/:slug`, folded by the backend beside the name. */
  relator_slug: string | null;
  outcome_codes: string[];
  legal_citations: string[];
  is_final_observed: boolean;
  session_number: string | null;
  session_date_raw: string | null;
  /** ISO date of the session (pauta header, else the ata's prose); absent where neither states one. */
  session_date?: string | null;
  /** Archive year of the session's ata, beside the prose date. */
  session_year: number | null;
  /** The "Decisão:" clause exists. Does not change meaning with the scope. */
  has_text: boolean;
  /** The whole ata item exists. */
  has_item_text: boolean;
  /**
   * Sum of the BRL amounts minuted on the decision, null when none. On a
   * deferred item (VIEW_REQUESTED_DEFERRED) an amount is minuted, not imposed.
   */
  fine_total_brl: number | null;
  /** Fine rows carrying only a UFIR amount: counted, never converted. */
  ufir_only_fines: number;
  is_unanimous: boolean | null;
  /** Vote rows minuted on the decision, as recorded. */
  votes: number;
  /**
   * Matches wrapped in [[hl]] sentinels, never HTML, and shown with diacritics
   * folded -- the same fold the search applies. Render through `Snippet`.
   */
  snippet: string | null;
}

/** One legal ground and how the decisions citing it resolved. */
export interface CadeCitationLine {
  citation: string;
  /** Final decisions citing this ground -- the line's denominator. */
  decisions: number;
  /** Code occurrences; multi-label, so shares can sum past 1.0. */
  outcomes: CategoryShare[];
  /** Citing decisions with at least one BRL fine row. A count, never a sum. */
  fined_decisions: number;
  /** Those rows; they concentrate -- one decision can carry dozens. */
  fine_rows: number;
}

export interface CadeCitationLines {
  lines: CadeCitationLine[];
}

export interface CadeDecisionList {
  as_of: AsOf;
  search: AnswerOut<CadeSearchReach>;
  /**
   * The tribunal's outcome record by legal ground, over citation-bearing
   * final decisions. PARTIAL while extraction covers a subset; refused when
   * no citation clears the display floor.
   */
  citation_lines: AnswerOut<CadeCitationLines>;
  items: CadeDecisionHit[];
  total: number;
  limit: number;
  offset: number;
  /** Rapporteur filter options, from the data. */
  relators: string[];
  /** Case-type options, scoped to cases carrying a decision and deduplicated
      on the folded spelling -- one option per type, not per registry spelling. */
  case_types: string[];
  /** The case_types naming the concentration track -- merger review renders
      apart from conduct enforcement, which is a different analysis. */
  concentration_case_types: string[];
  /** Outcome codes present on at least one decision, from the data. */
  outcome_codes: string[];
  /** Archive years of sessions that decided at least one case: the year filter. */
  years: number[];
}

export interface CadeDecisionQuery {
  query?: string | null;
  relator?: string | null;
  /** "Has text" in the scope searched: the clause, or the whole item. */
  onlyWithText?: boolean;
  caseType?: string | null;
  /** Merger review vs conduct enforcement -- different analyses, kept separable. */
  track?: 'concentration' | 'other' | null;
  outcomeCode?: string | null;
  /** The text searched: the "Decisão:" clause (default) or the whole ata item. */
  scope?: 'decision' | 'item';
  /** One legal citation, exactly as extracted -- the doctrine line's drill-through. */
  citation?: string | null;
  sessionYear?: number | null;
  onlyFinal?: boolean;
  /** One session's key, from the sessions list. */
  session?: string | null;
  limit?: number;
  offset?: number;
}

export interface CadeLabelCount {
  label: string;
  decisions: number;
}

export interface CadeRapporteurCaseload {
  name: string;
  /** URL key for `/cade/r/:slug`, folded by the backend beside the name. */
  slug: string;
  decisions: number;
  final_decisions: number;
  /** First and last archive year of a final decision as relator; the window the count is read against. */
  first_year: number | null;
  last_year: number | null;
  /** Final decisions carrying an outcome code. The outcome mix is over THIS. */
  classified: number;
  classified_share: number;
  /** Share of this rapporteur's final decisions in their largest case type. */
  concentration: number;
  outcomes: CadeLabelCount[];
  case_types: CadeLabelCount[];
}

export interface CadeBench {
  rapporteurs: CadeRapporteurCaseload[];
  /** Counted in the totals, held out of the table for sample size. */
  suppressed: number;
  people: number;
  final_decisions: number;
  final_with_rapporteur: number;
  attribution_share: number;
  threshold: number;
}

export interface CadeBenchMember {
  name: string;
  slug: string;
  /** First and last archive year present in EITHER role -- presence in the minutes, not a mandate. */
  first_year: number | null;
  last_year: number | null;
  relator_final: number;
  /** Minuted positions, recusals included. */
  votes: number;
  recused: number;
  sessions: number;
}

export interface CadeCommissioners {
  as_of: AsOf;
  /** Everyone named in a decision-making role: the overview's "Conselheiros" population, one row each. */
  roster: AnswerOut<CadeBenchMember[]>;
  bench: AnswerOut<CadeBench>;
  /** Reused verbatim from the overview. Both still refuse. */
  agreement_matrix: AnswerOut<DecisionMakerSample>;
  rapporteur_follow_rate: AnswerOut<DecisionMakerSample>;
}

/** One account, as the admin screen sees it. Never carries a password hash. */
export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  agencies: string[];
  /**
   * What the app ACTUALLY enforces. An admin reaches every corpus however empty
   * `agencies` is, so this is the honest answer and the array alone is not.
   */
  may_access_any: boolean;
  /** Successful logins only. Null is "none since the column existed". */
  last_login_at: string | null;
  created_at: string | null;
  /** The firm the account belongs to; null for none. */
  firm_id?: string | null;
}

export interface AdminFirm {
  id: string;
  name: string;
  members: number;
}

export interface AdminUserList {
  users: AdminUser[];
  /** The caller, so their own row can be marked and its controls disabled. */
  actor_id: string;
  firms?: AdminFirm[];
}

export interface AdminUserUpdate {
  role?: string;
  is_active?: boolean;
  agencies?: string[];
}

/** One of the four ANEEL tracks, present or absent for a given company. */
export interface AneelTrackPresence {
  track: 'enforcement' | 'ti' | 'tipe' | 'fees';
  present: boolean;
  /** Cases for the three case tracks; billing rows for the fee ledger. */
  records: number;
}

export interface AneelEntityReach {
  cnpj_digits: string;
  canonical_name: string;
  tracks: AneelTrackPresence[];
  /**
   * The source's own flag. NOT a validity judgement — it is `true` on exactly
   * the companies holding an infraction notice and `null` on the other 2,736,
   * which tracks the loader rather than the number. Never render it as a
   * verified/unverified badge the way the ANATEL profile does.
   */
  cnpj_valid: boolean | null;
  track_count: number;
  /** True for 2,375 of 3,803 companies — the commonest shape in the corpus. */
  fee_only: boolean;
}

export interface AneelEnforcementHistory {
  cases: number;
  decided: number;
  pending: number;
  charged_total: number;
  /** Over DECIDED cases only. Not comparable with `charged_total`. */
  decided_charged_total: number;
  decided_final_total: number;
  decided_relief: number;
  reduced: number;
  maintained: number;
  eliminated: number;
  increased: number;
}

export interface AneelFeeLedgerSummary {
  rows: number;
  paid: number;
  pending: number;
  other: number;
  billed_total: number;
  paid_total: number;
  earliest: string | null;
  latest: string | null;
}

export interface AneelRelatedEntity {
  cnpj_digits: string;
  canonical_name: string;
  relationship_type: string;
}

export interface AneelEntityCaseRow {
  notice_number: string;
  /** Slug computed in SQL. Link with it; never rebuild it here. */
  case_key: string;
  notice_date: string | null;
  nature: string | null;
  inspecting_agency: string | null;
  trajectory: string | null;
  charged_amount: number | null;
  /** Equal to `charged_amount` by construction on every undecided case. */
  final_amount: number | null;
  closure_date: string | null;
}

export interface AneelEntityTiRow {
  intimation_number: string;
  intimation_date: string | null;
  nature: string | null;
  penalty_types: string[];
  manifestation_outcome: string | null;
  closure_date: string | null;
}

export interface AneelEntityTipeRow {
  edital_penalty_number: string | null;
  inspection_process_number: string | null;
  penalty_type: string | null;
  original_fine_amount: number | null;
  fine_after_manifestation: number | null;
}

export interface AneelFineStatusRow {
  status: string;
  rows: number;
}

/**
 * This company's rows in the FINE ledger, matched on its CNPJ. How the
 * charges were collected -- never whether the company was fined.
 */
export interface AneelFineCollection {
  rows: number;
  linked_rows: number;
  statuses: AneelFineStatusRow[];
  with_days_late: number;
  late_rows: number;
  /** Days late over the late rows; null below the floor. */
  lateness: QuantileSummary | null;
  suspended_rows: number;
  due_total: number;
  paid_total: number;
}

/** One non-settled TFSEE credit state -- a levy's collection state, never a sanction. */
export interface AneelFeeCreditExposure {
  status: string;
  rows: number;
  earliest: string | null;
  latest: string | null;
}

export interface AneelEntity {
  as_of: AsOf;
  /** Always answered for a company that exists. */
  reach: AnswerOut<AneelEntityReach>;
  /**
   * These refuse when the company is absent from the track. The refusal is
   * about the COMPANY, not about what ANEEL's corpus can support.
   */
  enforcement: AnswerOut<AneelEnforcementHistory>;
  fees: AnswerOut<AneelFeeLedgerSummary>;
  fine_collection: AnswerOut<AneelFineCollection>;
  /** An EMPTY list is an answer: in the ledger, nothing outstanding. */
  fee_exposure: AnswerOut<AneelFeeCreditExposure[]>;
  related: AnswerOut<AneelRelatedEntity[]>;
  enforcement_rows: AneelEntityCaseRow[];
  ti_rows: AneelEntityTiRow[];
  tipe_rows: AneelEntityTipeRow[];
}

export interface AneelEntityDirectoryRow {
  cnpj_digits: string;
  canonical_name: string;
  enforcement_cases: number;
  ti_cases: number;
  tipe_cases: number;
  fee_rows: number;
  first_notice_year: number | null;
  last_notice_year: number | null;
  has_fee_exposure: boolean;
}

export interface AneelDirectory {
  entities: number;
  with_enforcement: number;
  with_two_or_more_cases: number;
  in_fee_ledger: number;
  fee_only: number;
  fee_only_share: number;
  single_track: number;
  all_four_tracks: number;
  with_fee_exposure: number;
  in_no_track: number;
}

export interface AneelEntities {
  as_of: AsOf;
  total: number;
  /** Ordered by name, never by a count. */
  items: AneelEntityDirectoryRow[];
  directory: AnswerOut<AneelDirectory>;
}

/** Two conselheiros, and how their minuted positions lined up. */
export interface CadePairRecord {
  name_a: string;
  name_b: string;
  name_a_slug: string;
  name_b_slug: string;
  /** Decisions where BOTH were minuted with a substantive position. */
  shared: number;
  /** Archive years of the first and last decision they were minuted on together. */
  first_year: number | null;
  last_year: number | null;
  both_outvoted: number;
  both_with_rapporteur: number;
  split: number;
  /**
   * The four breakdown fields sum to `shared`. Render them BESIDE the count and
   * never divided by it: the vocabulary describes each vote's relation to the
   * outcome, not agreement between the two members.
   */
  dissent_involved: number;
}

export interface CadeCoParticipation {
  pairs: CadePairRecord[];
  below_floor: number;
  total_pairs: number;
  people: number;
  /** Votes as recorded. */
  votes: number;
  /** Of those, the ones resolving to a canonical person. */
  votes_canonical: number;
  /** What the pairs rest on: one row per (decision, person) after the collapse. */
  votes_collapsed: number;
  decisions: number;
  decisions_with_votes: number;
  /** Decisions carrying two or more substantive votes -- where a pair can form. */
  decisions_pairable: number;
  recused_votes: number;
  recused_co_minutings: number;
  mutual_recusals: number;
  threshold: number;
  vote_coverage: number;
  pair_coverage: number;
}

/** One vote recorded against the table's decision, with its case. */
export interface CadeDissentInstance {
  voter: string;
  voter_slug: string;
  position: string;
  case_number: string;
  case_key: string;
  /** Key of the decision the dissent was minuted on -- the page it is read on. */
  decision_key: string;
  item_number: string | null;
  session_date: string | null;
  session_year: number | null;
  outcome_codes: string[];
}

export interface CadeMeritsMember {
  name: string;
  slug: string;
}

/** The votes on ONE decision, as recorded and as collapsed. */
export interface CadeDecisionVoteRecord {
  /** Vote rows as minuted. */
  rows: number;
  /** One per canonical person after the precedence collapse. */
  people: number;
  unresolved: number;
  recused: number;
  by_position: Record<string, number>;
  /** Who sat with a substantive position -- a set, never pairs. */
  on_the_merits: CadeMeritsMember[];
}

export interface CadeDecisionRecord {
  as_of: AsOf;
  case_number: string;
  case_key: string;
  case_type: string | null;
  filing_year: number | null;
  case_sessions: number;
  case_decisions: number;
  decision: CadeCaseDecision;
  vote_record: CadeDecisionVoteRecord;
}

export interface CadeVotedDecision {
  decision_key: string;
  case_number: string;
  case_key: string;
  item_number: string | null;
  case_type: string | null;
  session_year: number | null;
  session_date_raw: string | null;
  relator: string | null;
  relator_slug: string | null;
  outcome_codes: string[];
  is_final_observed: boolean;
  is_unanimous: boolean | null;
  /** Vote rows as minuted; `people` is distinct canonical names. */
  votes: number;
  people: number;
  losing: number;
  agrees_with_target: number;
  dissenting: number;
  recused: number;
}

/** One session of the archive: one ata file, and what it decided, counted. */
export interface CadeSession {
  /** What `/cade/decisions?session=` filters on. */
  session_key: string;
  session_number: string | null;
  session_type: string | null;
  session_date_raw: string | null;
  /** ISO date of the session (pauta header, else the ata's prose); absent where neither states one. */
  session_date?: string | null;
  session_year: number | null;
  source_file: string;
  decisions: number;
  final_decisions: number;
  with_outcome: number;
  with_fines: number;
  with_votes: number;
  cases: number;
  /** site: the ata CADE publishes; sei_reconstructed: a 2012-2017 ata rebuilt from SEI copies. */
  source_kind?: string;
}

/** Where a slice of the archive's atas came from, and the years it covers. */
export interface CadeSourceSessions {
  kind: string;
  sessions: number;
  decisions: number;
  first_year: number | null;
  last_year: number | null;
}

export interface CadeYearSessions {
  year: number;
  sessions: number;
  decisions: number;
}

export interface CadeSessionArchive {
  sessions: number;
  with_number: number;
  with_date: number;
  with_year: number;
  decisions: number;
  date_coverage: number;
  /** Sessions and decisions per archive-year folder, oldest first. */
  by_year: CadeYearSessions[];
  /** Lowest and highest ORDINARY session number present, null when none is numbered. */
  ordinary_first: number | null;
  ordinary_last: number | null;
  /** Numbers between them that no ata in the archive carries. */
  missing_numbers: number[];
  /** The archive per source of its atas; the numbering span above counts the portal's only. */
  by_source?: CadeSourceSessions[];
}

export interface CadeSessionList {
  as_of: AsOf;
  archive: AnswerOut<CadeSessionArchive>;
  items: CadeSession[];
  total: number;
  limit: number;
  offset: number;
}

export interface CadeVotedDecisionList {
  as_of: AsOf;
  total: number;
  limit: number;
  offset: number;
  items: CadeVotedDecision[];
}

export interface CadeVotes {
  as_of: AsOf;
  co_participation: AnswerOut<CadeCoParticipation>;
  /** Refused. The refusal carries what was measured, not an assertion. */
  agreement_rate: AnswerOut<null>;
  positions: AnswerOut<CategoryShare[]>;
  /** Raw instances, as minuted -- a review list, never a consistency measure. */
  dissent_instances: CadeDissentInstance[];
}

export interface CadeConselheiroDecision {
  case_number: string;
  case_key: string;
  /** The decision's natural key, for /cade/votes/d/:key. */
  decision_key: string;
  item_number: string | null;
  case_type: string | null;
  outcome_codes: string[];
  is_final_observed: boolean;
  session_date_raw: string | null;
  /** Archive year of the session's ata. */
  session_year: number | null;
  fine_total_brl: number | null;
}

export interface CadeVotePosition {
  position: string;
  votes: number;
}

export interface CadeConselheiroYear {
  year: number;
  decisions: number;
  final_decisions: number;
  /** Final decisions where a fine was recorded. A count. */
  with_fine: number;
}

export interface CadeConselheiroProfile {
  name: string;
  slug: string;
  /** Presence across both roles: first and last archive year, distinct sessions. Not a mandate record. */
  first_year: number | null;
  last_year: number | null;
  sessions: number;
  /** Their record as relator, per archive year. */
  by_year: CadeConselheiroYear[];
  decisions: number;
  final_decisions: number;
  classified: number;
  case_types: CadeLabelCount[];
  /** A COUNT of decisions that recorded a fine, never an amount. */
  decisions_with_fine: number;
  /** Finals homologating a TCC -- posture as a count, never a leniency score. */
  tcc_homologated: number;
  /** tcc_homologated / final_decisions: docket composition, not treatment. */
  tcc_share: number;
  /** Finals on case types that revisit a prior act (Recurso/Embargos/Revisão). */
  appeal_docket: number;
  votes: number;
  positions: CadeVotePosition[];
  recusals: number;
  corpus_final_decisions: number;
  corpus_final_with_rapporteur: number;
  people: number;
  threshold: number;
  classified_share: number;
  /** Share of their final decisions in one case type -- the docket confound. */
  concentration: number;
  bench_share: number;
  below_floor: boolean;
}

export interface CadeSeiLink {
  url: string;
  doc_type: string;
  doc_date: string | null;
}

export interface CadeThesis {
  /** conduct_type | theory_of_harm */
  field: string;
  code: string;
  cases: { case_number: string; case_key: string; quote: string | null }[];
}

export interface CadeRelatorNarrative {
  name: string;
  reported_decisions: number;
  reported_cases: number;
  /** Reported cases with a served coded field; null when the codings are not loaded. */
  coded_cases: number | null;
  theses: CadeThesis[];
  vistas: {
    case_number: string;
    case_key: string;
    session_date: string | null;
    days: number | null;
    next_outcomes: string[];
  }[];
  fines: {
    case_number: string;
    case_key: string;
    decision_key: string;
    session_year: number | null;
    /** Each minuted amount, as recorded; never summed. */
    amounts: number[];
    outcome_codes: string[];
    conduct: string | null;
    fine_base: string | null;
    fine_rates: string[];
    votes: CadeSeiLink[];
  }[];
  splits: {
    case_number: string;
    case_key: string;
    decision_key: string;
    session_year: number | null;
    outcome_codes: string[];
    positions: { name: string; position: string }[];
    votes: CadeSeiLink[];
  }[];
}

export interface CadeConselheiro {
  as_of: AsOf;
  profile: AnswerOut<CadeConselheiroProfile>;
  /** Refused below the floor; render with `scope="record"`. */
  outcome_mix: AnswerOut<CadeLabelCount[]>;
  /** The whole bench's mix, the column the person's is read beside. Shares over
      the classified final decisions with a relator; descriptive only. */
  corpus_mix: AnswerOut<CategoryShare[]>;
  items: CadeConselheiroDecision[];
  conselho?: PersonConselho | null;
  /** The record told as cases (theses, vistas, fines, split decisions); refused when none reported. */
  narrative?: AnswerOut<CadeRelatorNarrative> | null;
}

/** A party name as the CADE session archive recorded it. Never a company. */
export interface CadePartyListItem {
  name: string;
  slug: string;
  decisions: number;
  roles: string[];
  /** Distinct cases behind the decisions. */
  cases: number;
  /** Archive years of the sessions naming this string: the record's window. */
  first_year: number | null;
  last_year: number | null;
  /** A flag, never an amount. */
  has_attributed_fine: boolean;
  /** Digits and punctuation only: a clock time or a date read as a name. */
  is_artefact: boolean;
}

export interface CadePartyQuery {
  query?: string;
  limit?: number;
  offset?: number;
}

export interface CadePartyList {
  as_of: AsOf;
  identity: AnswerOut<CadePartyIdentity>;
  items: CadePartyListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CadePartyAppearance {
  case_number: string;
  case_key: string;
  /** The decision's natural key, for /cade/votes/d/:key. */
  decision_key: string;
  item_number: string | null;
  role: string | null;
  case_type: string | null;
  /** The outcome of the DECISION, never of this party. */
  outcome_codes: string[];
  is_final_observed: boolean;
  session_date_raw: string | null;
  /** Archive year of the session's ata. */
  session_year: number | null;
}

export interface CadePartySpelling {
  raw_name: string;
  match_method: string;
}

export interface CadeClusterSibling {
  name: string;
  slug: string;
  match_method: string | null;
}

export interface CadePartyAttribution {
  case_number: string;
  case_key: string;
  amount_brl: number | null;
  /** Withheld, not merely flagged, where the span failed its substring check. */
  evidence_span: string | null;
  evidence_verified: boolean;
  model_name: string;
  prompt_version: string;
  /** A regex cut this span out, so it verifies by construction. */
  from_pattern: boolean;
}

export interface CadePartyRoleCount {
  role: string;
  decisions: number;
}

/** Carries NO fine total, average or rate. See analytics/cade/party.py. */
export interface CadePartyRecord {
  name: string;
  slug: string;
  cnpj: string | null;
  decisions: number;
  final_decisions: number;
  cases: number;
  roles: CadePartyRoleCount[];
  spellings: CadePartySpelling[];
  cluster_siblings: CadeClusterSibling[];
  corpus_linked_parties: number;
  corpus_linked_decisions: number;
  /** The record's window: first and last archive year of the sessions naming it. */
  first_year: number | null;
  last_year: number | null;
  /** Digits and punctuation only -- a clock time or a date read as a name. */
  is_artefact: boolean;
  multi_role: boolean;
}

export interface CadePartyProfile {
  as_of: AsOf;
  identity: AnswerOut<CadePartyIdentity>;
  record: AnswerOut<CadePartyRecord>;
  /** NO_SUBSTRATE, rendered at CORPUS scope -- no party in this corpus has one. */
  outcome: AnswerOut<null>;
  /** Refused at RECORD scope: the corpus supports these, this name carries none. */
  attributions: AnswerOut<CadePartyAttribution[]>;
  appearances: CadePartyAppearance[];
  /** The CNPJ the ETL linked to this name on evidence; null when not linked. */
  cnpj_link?: CadePartyCnpjLink | null;
}

export interface CadePartyCnpjLink {
  cnpj: string;
  cnpj_formatted: string;
  /** The 8-digit root: the key of `/cade/company/:root`. */
  root: string;
  /** `sei_text` or `rf_exact_unique`. */
  method: string;
  quote: string | null;
  nup: string | null;
  legal_name: string | null;
  cnae_main: string | null;
}


// ANS types live in their own module; re-exported so callers keep one import path.
export type * from '@/services/ans-types';
export type * from '@/services/ibama-types';
export type * from '@/services/bacen-types';
export type * from '@/services/cvm-types';

// ---------------------------------------------------------------------------
// ANATEL Conselho Diretor track: model-derived decision patterns.
// Every figure names the registered run it was read from; rows below the
// ETL's sample floor arrive flagged `suppressed` and are listed, never ranked.
// ---------------------------------------------------------------------------

export interface ConselhoRun {
  run_id: string;
  model: string;
  model_version: string;
  n_obs: number;
  accepted: boolean;
  max_rhat: number | null;
  min_ess: number | null;
  divergences: number | null;
  built_at: string;
}

export interface ConselhoMetric {
  name: string;
  slug: string;
  scope: string;
  n: number;
  raw_rate: number | null;
  adjusted_effect: number | null;
  lower: number | null;
  upper: number | null;
  p_positive: number | null;
  suppressed: boolean;
}

export interface ConselhoPosition {
  name: string;
  slug: string;
  period: string;
  position: number | null;
  lower: number | null;
  upper: number | null;
  dissent_propensity: number | null;
  n_items: number;
  n_contested: number;
  suppressed: boolean;
}

export interface ConselhoPair {
  name_a: string;
  slug_a: string;
  name_b: string;
  slug_b: string;
  era: string;
  n_co_sat: number;
  n_agree: number;
  n_contested_co_sat: number;
  n_agree_contested: number;
  agreement_rate: number | null;
  agreement_rate_contested: number | null;
  suppressed: boolean;
  /** 90% Wilson intervals the platform computes from the counts (ANATEL). */
  lower?: number | null;
  upper?: number | null;
  lower_contested?: number | null;
  upper_contested?: number | null;
}

export interface ConselhoRosterMember {
  name: string;
  slug: string;
  office_code: string | null;
  first_seen: string | null;
  last_seen: string | null;
  n_relatorias: number;
  n_presences: number;
  n_analises: number;
  n_votos: number;
  roles: string[];
}

export interface ConselhoRubric {
  name: string;
  slug: string;
  dimension: string;
  n: number;
  raw_mean: number | null;
  effect: number | null;
  lower: number | null;
  upper: number | null;
  p_positive: number | null;
  suppressed: boolean;
}

export interface ConselhoSgCoding {
  dimension: string;
  value: string;
  evidence_span: string | null;
  evidence_verified: boolean;
}

export interface ConselhoSgDocument {
  sei_id: string;
  nup: string;
  title: string;
  unit: string | null;
  kind: string;
  pages: number | null;
  rubric_version: string;
  public_version: boolean;
  author_layer: string;
  kappa: string;
  codings: ConselhoSgCoding[];
}

export interface ConselhoSeiIssueVote {
  issue_slug: string;
  issue_label: string;
  majority_outcome: string | null;
  voter_canonical: string;
  position: string;
  side: string;
  is_relator: boolean;
  evidence_span: string | null;
  evidence_verified: boolean;
}

export interface ConselhoSeiMinutes {
  nup: string;
  n_documents: number;
  n_certidoes: number;
  n_votos: number;
  collapse_refused: boolean;
  issues: ConselhoSeiIssueVote[];
}

export interface ConselhoValidation {
  check: string;
  statistic: string;
  value: number | null;
  lower: number | null;
  upper: number | null;
  n: number;
  note: string | null;
}

export interface ConselhoBalanceRow {
  stratum: string;
  feature: string;
  test: string;
  statistic: number | null;
  p_value: number | null;
  n: number;
}

export interface ConselhoEvidence {
  level: 'descriptive' | 'adjusted' | 'adjusted_balanced';
  pooled_lift: number | null;
  max_year_lift: number | null;
  significant_tests: number;
  tests: number;
  /** Tests surviving Bonferroni; absent where the track does not compute it (CADE). */
  bonferroni_significant?: number | null;
  /** CADE only: a relator x year balance test survives Bonferroni. */
  year_imbalanced?: boolean | null;
  rows: ConselhoBalanceRow[];
}

/** CADE: pedidos de vista -- who asked, and what the vista cases ended as. */
export interface ConselhoVista {
  requesters: { name: string; slug: string; resolved: boolean; decisions: number }[];
  unresolved_decisions: number;
  follow: {
    vista_cases: number;
    resolved: number;
    non_unanimous: number;
    baseline_final: number;
    baseline_non_unanimous: number;
    with_proposed_fine: number;
    fine_absent: number;
    fine_raised: number;
    fine_lowered: number;
    fine_same: number;
  };
  unanimity: { flagged_unanimous: number; text_names_outvoted: number };
  non_unanimous_share: number | null;
  non_unanimous_lower: number | null;
  non_unanimous_upper: number | null;
  baseline_share: number | null;
  baseline_lower: number | null;
  baseline_upper: number | null;
}

/** CADE: how often one member ended on the losing side of a complete item,
 * against what the all-member rate of their own years predicts. */
export interface ConselhoMemberOutvoted {
  name: string;
  slug: string;
  items: number;
  outvoted: number;
  first_year: number;
  last_year: number;
  outvoted_share: number | null;
  outvoted_lower: number | null;
  outvoted_upper: number | null;
  expected_outvoted: number;
  observed_over_expected: number | null;
  suppressed: boolean;
}

export type ConselhoItemMetric = 'follow' | 'outvoted' | 'pair';

/** CADE: one complete item behind a member's figure. */
export interface ConselhoMemberItem {
  process_number: string | null;
  source_file: string;
  item_number: string;
  year: number | null;
  side: string;
  other_side: string | null;
  relator: string | null;
  relator_side: string | null;
  contested: boolean;
  counted: boolean;
  case_key?: string | null;
}

export interface ConselhoMemberItems {
  as_of: AsOf;
  items: AnswerOut<{
    metric: ConselhoItemMetric;
    name: string;
    other: string | null;
    counted: number;
    items: ConselhoMemberItem[];
  }>;
}

/** CADE: how often the plenary outvoted one relator, from the ruling text. */
export interface ConselhoRelatorOutvoted {
  name: string;
  slug: string;
  decided: number;
  outvoted: number;
  outright: number;
  first_year: number;
  last_year: number;
  outvoted_share: number | null;
  outvoted_lower: number | null;
  outvoted_upper: number | null;
  expected_outvoted: number;
  observed_over_expected: number | null;
  suppressed: boolean;
}

/** CADE: one relator's pauta docket against its own years' corpus rate. */
export interface ConselhoDocketRow {
  name: string;
  slug: string;
  processes: number;
  carried: number;
  redistributed: number;
  first_year: number;
  last_year: number;
  carried_share: number | null;
  carried_lower: number | null;
  carried_upper: number | null;
  expected_carried: number;
  observed_over_expected: number | null;
  suppressed: boolean;
}

/** CADE: how the minutes name one member where they were not relator. */
export interface ConselhoMinutesRow {
  name: string;
  slug: string;
  named: number;
  sided: number;
  losing: number;
  agreeing: number;
  dissenting: number;
  recused: number;
  losing_share: number | null;
  losing_lower: number | null;
  losing_upper: number | null;
  suppressed: boolean;
}

/** Whether a bench is ordered by effect, and if not, why. */
export interface ConselhoOrdering {
  ranked: boolean;
  reason: 'no_interval_excludes_zero' | 'effect_unstable' | 'era_aligned' | null;
  listed: number;
  excluding_zero: number;
  /** Spearman rho between the effects and each member's first appearance. */
  era_rho?: number | null;
  era_n?: number;
}

/** What the severity run's validation says about reading an effect as the person's. */
export interface ConselhoReading {
  placebo_p: number | null;
  split_r: number | null;
  split_n: number;
  auc_lift: number | null;
  auc_n: number;
  sensitivity_rho: number | null;
  sensitivity_n: number;
  stable: boolean | null;
}

/** One member's scoped rows above the floor, those excluding zero, and chance's share at 90%. */
export interface ConselhoScopeTally {
  listed: number;
  excluding_zero: number;
  expected_by_chance: number;
}

export interface ConselhoVariance {
  metric: string;
  value: number;
  lower: number;
  upper: number;
}

/** The parameters a methodology page quotes, from the constants the bench applies. */
export interface ConselhoMethod {
  interval_level: number;
  floors: Record<string, number>;
  /** CADE: years whose minutes list who sat (complete roll call). */
  complete_years?: number[] | null;
  /** ANATEL: the era rule behind a by-name order. */
  era_rho_limit?: number | null;
  era_min_members?: number | null;
}

export interface ConselhoBench {
  as_of: AsOf;
  tables_present: boolean;
  method?: ConselhoMethod | null;
  evidence: ConselhoEvidence;
  roster: AnswerOut<ConselhoRosterMember[]>;
  severity_run: ConselhoRun | null;
  severity: AnswerOut<ConselhoMetric[]>;
  severity_pp: AnswerOut<ConselhoMetric[]>;
  severity_variance: ConselhoVariance[];
  validation: AnswerOut<ConselhoValidation[]>;
  person_reading?: ConselhoReading | null;
  severity_order?: ConselhoOrdering | null;
  follow_order?: ConselhoOrdering | null;
  follow_run: ConselhoRun | null;
  dissent: AnswerOut<ConselhoMetric[]>;
  follow: AnswerOut<ConselhoMetric[]>;
  /** ANATEL only: the relator's adjusted propensity to be followed. */
  followed?: AnswerOut<ConselhoMetric[]> | null;
  followed_order?: ConselhoOrdering | null;
  /** CADE only: the named-minutes record that replaced a follow share. */
  minutes_record?: AnswerOut<ConselhoMinutesRow[]> | null;
  /** CADE only: pedidos de vista, and the 1997-2026 pauta docket per relator. */
  vista?: AnswerOut<ConselhoVista> | null;
  docket?: AnswerOut<ConselhoDocketRow[]> | null;
  /** CADE only: how often the plenary outvoted each relator (ruling text). */
  relator_outvoted?: AnswerOut<ConselhoRelatorOutvoted[]> | null;
  /** CADE only: how often each member was outvoted on complete items (O/E). */
  outvoted?: AnswerOut<ConselhoMemberOutvoted[]> | null;
  pairs: AnswerOut<ConselhoPair[]>;
  position_run: ConselhoRun | null;
  positions: AnswerOut<ConselhoPosition[]>;
  propensity_run: ConselhoRun | null;
  propensities: AnswerOut<ConselhoPosition[]>;
  wordfish_run: ConselhoRun | null;
  wordfish: AnswerOut<ConselhoMetric[]>;
  projection_run: ConselhoRun | null;
  style_residual: AnswerOut<ConselhoMetric[]>;
  rubric_run: ConselhoRun | null;
  rubric: AnswerOut<ConselhoRubric[]>;
  citation_family_run?: ConselhoRun | null;
  citation_families?: AnswerOut<ConselhoMetric[]> | null;
  citation_family_reach?: { decisions: number; labels: number } | null;
  sg_document?: AnswerOut<ConselhoSgDocument> | null;
  sei_minutes?: AnswerOut<ConselhoSeiMinutes> | null;
}

export interface ConselhoContestedItem {
  process_number: string;
  process_key: string;
  item_label: string;
  session_date: string | null;
  tematica: string | null;
  relator: string | null;
  relator_slug: string | null;
  direction: string | null;
  unanimity: string;
  side: string | null;
  position: string | null;
  n_sitting: number;
  n_minority: number;
}

export interface ConselhoItems {
  as_of: AsOf;
  total: number;
  items: ConselhoContestedItem[];
}

export interface ConselhoScopeCase {
  process_number: string | null;
  process_key: string | null;
  acordao: string | null;
  date: string | null;
  tematica: string | null;
  subject: string;
  party_class: string | null;
  direction: string;
  unanimity: string | null;
}

/** The acórdãos one severity figure rests on, and the bench over the same scope. */
export interface ConselhoScopeCases {
  as_of: AsOf;
  name: string;
  slug: string;
  scope: string;
  run: ConselhoRun | null;
  metric: ConselhoMetric | null;
  cases: AnswerOut<{
    cases: ConselhoScopeCase[];
    favorable: number;
    bench_cases: number;
    bench_favorable: number;
  }>;
}

export interface ConselhoDocument {
  doc_kind: string;
  doc_number: string | null;
  document_date: string | null;
  process_number: string | null;
  process_key: string | null;
  voto_type: string | null;
  tematica: string | null;
  linked: boolean;
}

export interface PersonConselho {
  tables_present: boolean;
  evidence: ConselhoEvidence;
  person_reading?: ConselhoReading | null;
  severity_run: ConselhoRun | null;
  severity: AnswerOut<ConselhoMetric[]>;
  severity_tally?: ConselhoScopeTally | null;
  follow_run: ConselhoRun | null;
  follow: AnswerOut<ConselhoMetric[]>;
  follow_tally?: ConselhoScopeTally | null;
  followed?: AnswerOut<ConselhoMetric[]> | null;
  followed_order?: ConselhoOrdering | null;
  minutes_record?: AnswerOut<ConselhoMinutesRow[]> | null;
  relator_outvoted?: AnswerOut<ConselhoRelatorOutvoted[]> | null;
  /** CADE only: the member's outvoted row and the pairs they belong to. */
  outvoted?: AnswerOut<ConselhoMemberOutvoted[]> | null;
  pairs?: AnswerOut<ConselhoPair[]> | null;
  position_run: ConselhoRun | null;
  positions: AnswerOut<ConselhoPosition[]>;
  propensities: AnswerOut<ConselhoPosition[]>;
  contested_items: ConselhoContestedItem[];
  documents: ConselhoDocument[];
  wordfish_run: ConselhoRun | null;
  wordfish: AnswerOut<ConselhoMetric[]>;
  projection_run: ConselhoRun | null;
  style_residual: AnswerOut<ConselhoMetric[]>;
  rubric_run: ConselhoRun | null;
  rubric: AnswerOut<ConselhoRubric[]>;
  /** One per locale (pt/en/es); see PersonReportCard. Absent on older payloads. */
  reports?: Record<string, PersonReportOut>;
}

/** One fact a report paragraph cites, and the panel on the page that shows it. */
export interface PersonReportFactOut {
  code: string;
  panel: string;
}

export interface PersonReportParagraphOut {
  text: string;
  facts: PersonReportFactOut[];
}

export interface PersonReportOut {
  status: 'current' | 'stale' | 'not_generated';
  paragraphs: PersonReportParagraphOut[];
  model: string | null;
  prompt_version: string | null;
  generated_at: string | null;
}

export interface CadePautaItem {
  case_number: string | null;
  /** Digits of the case number; set only where the case page exists. */
  case_key: string | null;
  case_type: string | null;
  relator: string | null;
  /** Where the ARCHIVE stands on the item -- never a forecast. */
  standing: 'decided_here' | 'decided_elsewhere' | 'not_in_archive';
}

export interface CadePautaSession {
  number: number | null;
  kind: string | null;
  date: string;
  time: string | null;
  items: CadePautaItem[];
}

export interface CadeRecentPautas {
  sessions: CadePautaSession[];
  latest_date: string;
  age_days: number;
  stale: boolean;
  total_sessions: number;
  dated_sessions: number;
  flagged_sessions: number;
}

export interface CadePautas {
  as_of: AsOf;
  pautas: AnswerOut<CadeRecentPautas>;
}

export interface CadeAudioRecording {
  /** URL key: 2025-O-242 (ordinary) or 2025-E-4 (extraordinary). */
  key: string;
  year: number;
  kind: string | null;
  number: number | null;
  /** The day in CADE's own file name for the recording. */
  session_date: string | null;
  /** CADE's public URL for the recording. */
  audio_url: string | null;
  /** 1..n when the session was recorded in several files; null for a whole-session file. */
  part?: number | null;
}

export interface CadeAudioCoverage {
  recordings_available: number;
  recordings_transcribed: number;
  hours: number;
  segments: number;
  years: number[];
}

export interface CadeAudioHit {
  recording: CadeAudioRecording;
  start_s: number;
  /** The passage, accents intact, matched words wrapped in [[hl]] sentinels. */
  text_marked: string;
  before: string | null;
  after: string | null;
  /** Anchor of the passage in the recording's full transcript. */
  seg: number;
}

export interface CadeAudioSearch {
  hits: CadeAudioHit[];
  total: number;
  coverage: CadeAudioCoverage;
  no_searchable_terms: boolean;
  expanded?: CadeExpansion[];
}

export interface CadeAudioSearchResponse {
  as_of: AsOf;
  search: AnswerOut<CadeAudioSearch>;
}

export interface CadeAudioRecordingRow {
  recording: CadeAudioRecording;
  duration_s: number | null;
  segments: number;
  results: number;
  vote_calls: number;
  recusals: number;
}

export interface CadeAudioRecordingList {
  as_of: AsOf;
  coverage: CadeAudioCoverage | null;
  recordings: AnswerOut<CadeAudioRecordingRow[]>;
}

export interface CadeSpokenName {
  spoken: string;
  /** The roster member it resolves to, when exactly one. */
  canonical: string | null;
}

export interface CadeSpokenEvent {
  event_type: 'result' | 'vote_call' | 'recusal';
  start_s: number;
  end_s: number;
  /** Exact words from the transcript, verified when extracted. */
  quote: string;
  complete: boolean | null;
  outcome_kind: string | null;
  action_text: string | null;
  dissenters: CadeSpokenName[];
  voter: CadeSpokenName | null;
  position: string | null;
  target: CadeSpokenName | null;
  seg_from: number;
  seg_to: number;
}

export interface CadeRecordingVotes {
  recording: CadeAudioRecording;
  duration_s: number | null;
  results: CadeSpokenEvent[];
  vote_calls: CadeSpokenEvent[];
  recusals: CadeSpokenEvent[];
}

export interface CadeAudioRecordingDetail {
  as_of: AsOf;
  votes: AnswerOut<CadeRecordingVotes>;
}

export interface CadeTranscriptSegment {
  seg: number;
  start_s: number;
  end_s: number;
  text: string;
}

export interface CadeEventSpan {
  event_type: 'result' | 'vote_call' | 'recusal';
  seg_from: number;
  seg_to: number;
}

export interface CadeTranscript {
  recording: CadeAudioRecording;
  duration_s: number | null;
  segments: CadeTranscriptSegment[];
  events: CadeEventSpan[];
  /** Segments left out because the model produced them by echoing its own prompt. */
  withheld: number;
}

export interface CadeAudioTranscript {
  as_of: AsOf;
  transcript: AnswerOut<CadeTranscript>;
}

export interface CadePositionRow {
  case_number: string;
  /** Digits for /cade/c/:key; null where the protocol is not in the session archive. */
  case_key: string | null;
  procedure: string | null;
  conducts: string[];
  sg_outcome: string | null;
  tribunal_outcome: string | null;
  decision_year: number | null;
  /** The register's total applied to the protocol (per-party maximum, summed). */
  fine_brl: number | null;
  party_rows: number;
}

export interface CadePositionList {
  rows: CadePositionRow[];
  total: number;
  with_fine: number;
  decided: number;
}

export interface CadePlacement {
  n: number;
  p25: number;
  median: number;
  p75: number;
  p90: number;
  maximum: number;
  amount: number | null;
  below: number | null;
  at_or_above: number | null;
}

export interface CadePositioning {
  as_of: AsOf;
  cases: AnswerOut<CadePositionList>;
  placement: AnswerOut<CadePlacement>;
  procedures: { procedure: string; protocols: number }[];
  conducts: string[];
  /** Where a TCC contribution falls among every TCC in the register (not narrowed by conduct). */
  tcc?: AnswerOut<CadePlacement> | null;
}


export interface CadeTopicPassage {
  recording: CadeAudioRecording;
  topic: string;
  start_s: number;
  end_s: number;
  /** Anchor of the passage in the recording's full transcript. */
  seg: number;
  /** The passage, accents intact, matched words wrapped in [[hl]] sentinels. */
  text_marked: string;
  terms: string[];
}

export interface CadeTopicCount {
  topic: string;
  passages: number;
  recordings: number;
}

export interface CadeTopicList {
  topic: string | null;
  recording: string | null;
  passages: CadeTopicPassage[];
  total: number;
  counts: CadeTopicCount[];
  coverage: CadeAudioCoverage;
}

export interface CadeAudioTopics {
  as_of: AsOf;
  topics: AnswerOut<CadeTopicList>;
}


export interface CadeCaseTalk {
  recording: CadeAudioRecording;
  pauta_item: number;
  /** judged | postponed | withdrawn. Only a judged item has a stretch of audio. */
  status: 'judged' | 'postponed' | 'withdrawn';
  confidence: 'high' | 'medium' | null;
  start_s: number;
  end_s: number;
  seg_from: number;
  seg_to: number;
  /** The sentence that placed the item, as transcribed. */
  call_quote: string;
  passages: CadeTopicPassage[];
  /** Called together with other cases: the stretch covers all of them. */
  joint: boolean;
  /** TCC and remedy terms as HEARD: leads with their words, never coded fields. */
  spoken?: CadeSpokenTerm[];
}

export interface CadeSpokenTerm {
  kind:
    | 'contribution_amount'
    | 'structural_remedy'
    | 'behavioural_remedy'
    | 'trustee'
    | 'duration';
  /** BRL for an amount, months for a duration. */
  value: number | null;
  unit: 'BRL' | 'months' | null;
  /** The words it was read from, in the [[hl]] markers. */
  text_marked: string;
  seg: number;
  start_s: number;
  /** Read out in the president's ruling sentence. */
  ruling: boolean;
}

export interface CadeCaseAudioValue {
  talks: CadeCaseTalk[];
  pauta_items: number;
  placed_items: number;
}

export interface CadeCaseAudio {
  as_of: AsOf;
  audio: AnswerOut<CadeCaseAudioValue>;
}

export interface CadeVistaRow {
  case_number: string;
  case_key: string;
  item_number: string | null;
  session_date: string | null;
  /** The roster name where the ata's name resolved, else as minuted, else null. */
  requester: string | null;
  requester_resolved: boolean;
  next_date: string | null;
  /** Days to the case's next archived appearance; null while the case is open. */
  days: number | null;
  next_outcomes: string[];
}

export interface CadeVistaRequester {
  name: string;
  resolved: boolean;
  requests: number;
  returned: number;
  /** Withheld (null) below the floor of returned vistas. */
  median_days: number | null;
}

export interface CadeVistaDelay {
  n: number;
  p25: number;
  median: number;
  p75: number;
  p90: number;
  minimum: number;
  maximum: number;
}

export interface CadeVistaList {
  rows: CadeVistaRow[];
  items: number;
  cases: number;
  returned: number;
  open_items: number;
  unnamed: number;
  delay: CadeVistaDelay | null;
  requesters: CadeVistaRequester[];
  min_requester_returns: number;
}

export interface CadeVistas {
  as_of: AsOf;
  vistas: AnswerOut<CadeVistaList>;
}

export interface CadeDossierPauta {
  session_number: number | null;
  session_kind: string | null;
  session_date: string;
  relator: string | null;
  case_type_label: string | null;
  /** Where the archive stands on the item; never a forecast. */
  standing: 'decided_here' | 'decided_elsewhere' | 'not_in_archive' | null;
}

export interface CadeDossierHistoryRow {
  session_date: string | null;
  session_number: string | null;
  item_number: string | null;
  outcomes: string[];
  relator: string | null;
  is_final: boolean;
  view_request: boolean;
  requester: string | null;
}

export interface CadeOutcomeShare {
  code: string;
  count: number;
}

export interface CadeDossierCase {
  case_number: string;
  case_key: string;
  session_date: string | null;
  outcomes: string[];
}

export interface CadeDossierRelator {
  relator: string;
  case_type_label: string;
  relator_finals: number;
  corpus_finals: number;
  relator_outcomes: CadeOutcomeShare[];
  corpus_outcomes: CadeOutcomeShare[];
  shares_shown: boolean;
  min_finals: number;
  cases: CadeDossierCase[];
}

export interface CadeDossierRegister {
  case_number: string;
  procedure: string | null;
  conducts: string[];
  sg_outcome: string | null;
  tribunal_outcome: string | null;
  decision_year: number | null;
  fine_brl: number | null;
  contribution_brl: number | null;
}

export interface CadeDossier {
  as_of: AsOf;
  case_number: string;
  case_key: string;
  in_archive: boolean;
  pauta: CadeDossierPauta | null;
  history: AnswerOut<CadeDossierHistoryRow[]>;
  relator: AnswerOut<CadeDossierRelator>;
  register_row: AnswerOut<CadeDossierRegister>;
  placement: AnswerOut<CadePlacement>;
  /** One timeline merged from the register, SEI and the archive, each event with its source. */
  timeline?: CadeCaseEvent[];
  parties?: { name: string; roles: string[]; cnpj: string | null; legal_name: string | null }[];
  codings?: AnswerOut<CadeCaseCodings> | null;
}

export interface CadeCaseEvent {
  when: string | null;
  /** register | sei | archive */
  source: string;
  kind: string;
  detail: string | null;
  session_number: string | null;
  url: string | null;
  requester: string | null;
}

export type CadeJudicialLookup =
  | 'found'
  | 'not_found'
  | 'not_configured'
  | 'unsupported_court'
  | 'unavailable';

export interface CadeCourtRecord {
  court: string | null;
  organ: string | null;
  class_name: string | null;
  filed: string | null;
  subjects: string[];
  /** Newest first. No outcome is derived from them. */
  movements: { name: string; when: string | null }[];
  updated: string | null;
}

export interface CadeJudicialCase {
  link_id: string;
  cnj_number: string;
  court_label: string;
  source: 'ata' | 'sei' | 'client' | 'court' | 'other';
  evidence: string;
  added_on: string;
  can_remove: boolean;
  lookup: CadeJudicialLookup;
  record: CadeCourtRecord | null;
}

export interface CadeJudicialList {
  cases: CadeJudicialCase[];
  case_number: string;
  can_add: boolean;
}

export interface CadeJudicial {
  as_of: AsOf;
  can_add: boolean;
  judicial: AnswerOut<CadeJudicialList>;
  /** Court decisions CADE filed in the case's SEI record; null when the ledger is not loaded. */
  sei_court?: CadeSeiCourtDoc[] | null;
}

export interface CadeSeiCourtDoc {
  sei_doc_number: string;
  doc_type: string;
  doc_date: string | null;
  unit: string | null;
  url: string | null;
}

/** A company (CNPJ root) the company search offers. */
export interface CadeCompanyCandidate {
  root: string;
  root_formatted: string;
  legal_name: string | null;
  archive_names: number;
  interested_processes: number;
}

export interface CadeCompanySearch {
  as_of: AsOf;
  kind: 'root' | 'name' | 'invalid_cnpj' | 'empty' | 'no_tables';
  candidates: CadeCompanyCandidate[];
}

export interface CadeRegistryCard {
  cnpj: string;
  cnpj_formatted: string;
  legal_name: string | null;
  trade_name: string | null;
  /** The Receita's code (01-08) or the API's word (ATIVA, BAIXADA ...). */
  status: string | null;
  status_date: string | null;
  cnae_main: string | null;
  cnae_secondary: string[];
  uf: string | null;
  municipality: string | null;
  legal_nature: string | null;
  size: string | null;
  head_office: boolean | null;
  source: string;
  fetched_at: string | null;
}

export interface CadeCompanyAppearance {
  case_number: string;
  case_key: string;
  decision_key: string;
  item_number: string | null;
  party_name: string;
  role: string | null;
  case_type: string | null;
  /** The DECISION's outcome, never this company's result. */
  outcome_codes: string[];
  is_final: boolean;
  session_date: string | null;
  session_year: number | null;
}

export interface CadeCompanyAttribution {
  case_number: string;
  case_key: string;
  party_name: string;
  amount_brl: number | null;
  evidence_span: string | null;
  evidence_verified: boolean;
}

export interface CadeCompanyInterested {
  nup: string;
  process_type: string | null;
  registered: string | null;
  name_as_listed: string;
  case_key: string | null;
}

export interface CadeCompanyEvidence {
  name: string;
  cnpj: string;
  method: 'sei_text' | 'rf_exact_unique';
  quote: string | null;
  nup: string | null;
  sei_doc_number: string | null;
}

export interface CadeCompanyCheck {
  root: string;
  root_formatted: string;
  archive_cases: number;
  registry: CadeRegistryCard[];
  appearances: CadeCompanyAppearance[];
  attributions: CadeCompanyAttribution[];
  interested: CadeCompanyInterested[];
  evidence: CadeCompanyEvidence[];
}

export interface CadeCompany {
  as_of: AsOf;
  root: string;
  check: AnswerOut<CadeCompanyCheck>;
}

/** One decision document the document search found, with its best passage. */
export interface CadeDocumentRow {
  nup: string;
  /** Archive case page key when the process is in the session archive. */
  case_key: string | null;
  sei_doc_number: string;
  doc_type: string;
  kind: string | null;
  doc_date: string | null;
  unit: string | null;
  /** 1-based page of the passage shown. */
  page: number;
  passage_no: number;
  /** Which retrieval found the document. */
  match: 'exact' | 'meaning' | 'both';
  /** The passage's page was read by OCR. */
  from_ocr: boolean;
  /** [[hl]] markers when matched exactly (folded text); plain text when found by meaning. */
  snippet: string;
  exact_passages: number;
  url: string | null;
  read_on: string | null;
}

export interface CadeDocumentSearch {
  mode: 'hybrid' | 'exact' | 'meaning';
  rows: CadeDocumentRow[];
  /** Null when the ranking ran out of time and the matches are listed unranked. */
  exact_documents: number | null;
  /** False when the exact matches are listed newest first instead of by relevance. */
  exact_ranked: boolean;
  meaning_used: boolean;
  meaning_status: 'available' | 'not_configured' | 'unavailable' | 'no_vectors';
  expansions: CadeExpansion[];
  no_searchable_terms: boolean;
}

export interface CadeDocuments {
  as_of: AsOf;
  search: AnswerOut<CadeDocumentSearch>;
}

export interface CadeDocumentQuery {
  query?: string;
  mode?: 'hybrid' | 'exact' | 'meaning';
  kind?: string;
  year_from?: number;
  year_to?: number;
  process?: string;
}

/** One milestone document CADE's public SEI lists for a process. */
export interface CadeSeiDocument {
  sei_doc_number: string;
  /** Milestone kind, from analytics.cade.sei.DOC_KINDS. */
  kind: string;
  /** SEI's own document type, as listed. */
  doc_type: string;
  doc_date: string | null;
  unit: string | null;
  /** SEI's public address: an access token that may expire. */
  url: string | null;
  /** The day the ETL read the address. */
  read_on: string | null;
  /** Votes only: the archive session the vote is placed under by date. */
  session_date: string | null;
}

export interface CadeSeiSessionVotes {
  session_date: string;
  votes: CadeSeiDocument[];
}

export interface CadeCaseSei {
  nup: string;
  documents_total: number;
  milestones: CadeSeiDocument[];
  sessions: CadeSeiSessionVotes[];
  votes_total: number;
  votes_placed: number;
}

export interface CadeSei {
  as_of: AsOf;
  case_key: string;
  /** The placement window, so the rule stated on screen is the rule the backend applied. */
  vote_after_days: number;
  vote_before_days: number;
  sei: AnswerOut<CadeCaseSei>;
}

export interface CadeJudicialLinkIn {
  cnj_number: string;
  source: 'ata' | 'sei' | 'client' | 'court' | 'other';
  evidence: string;
}
